// A single shift (level session) in the Flying Saucer teahouse.
import { Scene, Camera, Node, Light } from '../engine/renderer.js';
import { col, lerp, clamp, pick, rand } from '../engine/math.js';
import { audio } from '../engine/audio.js';
import { input } from '../engine/input.js';
import { LEVELS, RECIPES, PERKS, INGREDIENTS, SLUDGE, SPECIES, SPECTATOR_COLOR, crewScale } from './data.js';
import { Station, stationKind, COUNTER_H } from './stations.js';
import { Ingredient, Cup, Vessel, Extinguisher, BobaCup, Straw, servable } from './items.js';
import { Chef } from './chef.js';
import { Guest } from './guest.js';
import { buildWorld } from './world.js';
import * as M from './models.js';
import { previewAction, coachHint } from './coach.js';
import { recordingHud, packEmit } from '../net/sync.js';
import { Helper, delegateTasks, isBot, DELEGATE_TASKS } from './helper.js';

const WALK = '.1234@&=';
export const BELT_SPEED = 0.6;
const BELT_VANISH = 0.4; // belt travel (in tiles) over which an item shrinks away at the end of the line

export class Game {
  /** opts.infinite: an infinite shift, which never closes and runs until someone ends it from the pause menu. */
  constructor(app, levelIndex, players, net = null, spectators = [], opts = {}) {
    this.app = app;
    this.levelIndex = levelIndex;
    this.level = LEVELS[levelIndex];
    this.theme = this.level.theme;
    this.players = players; // the chefs: [{device}]; online also {peer, dev}: whose player it is, and their device
    // people watching instead of cooking (same shape): no chef, but they own bots and steer them with Delegate
    this.spectators = spectators;
    // solo (one person here, cooking or watching, who uses any keyboard or controller) is worked out with the bots below
    this.solo = false;
    this.helpers = [];
    this.wheel = null; // the Delegate wheel, while its key is held
    // the bot helpers are shared out between the people playing (see _assignBots), and each person's Delegate
    // choices steer only their own bots: owner key -> {task: on}. Everyone's own choices are kept in their own save.
    const sv = app.save;
    sv.delegateBy = sv.delegateBy || {};
    if (sv.delegate && !sv.delegateBy.main) sv.delegateBy.main = sv.delegate; // (choices from before bots had owners)
    delete sv.delegate;
    this.delegateBy = {};
    this.botOwner = {}; // chef index -> owner key, for each bot
    // online: the host runs the shift and sends it out; a guest's game mirrors it (see src/net/)
    this.net = net;
    this.mirror = !!net && !net.isHost;
    this.netEvents = [];
    this.netItems = new Map();
    this.darkTarget = 0;
    this.scene = new Scene();
    this.camera = new Camera();
    this.hud = app.hud;
    this.time = 0;
    this.infinite = !!opts.infinite;
    // an infinite shift has all the time in the world: no closing time, no last-orders rush
    this.timeLeft = this.infinite ? Infinity : this.level.time;
    this.played = 0; // seconds of play so far (an infinite shift's clock counts this up)
    this.state = 'intro'; // intro -> countdown -> play -> end
    this.stateT = 0;
    this.coins = 0;
    this.tips = 0;
    this.served = 0;
    this.failed = 0;
    this.streak = 0;
    this.perks = {};
    this.guests = [];
    this.floorItems = [];
    this.projectiles = [];
    this.vessels = [];
    this.spawnTimer = 2.5;
    this.darkness = 0;
    this.shake = 0;
    this.patienceScale = players.length === 1 ? 1.15 : players.length >= 3 ? 0.9 : 1;
    this.twist = this.level.twist;
    this.events = { flareT: 30, flareWarn: 0, blackT: 38, blackDur: 0, bridgeT: 18, bridgePhase: 'ext' };
    this.recentOrders = [];
    this._setupScene();
    this._parseMap();
    this.world = buildWorld(this);
    this._spawnChefs();
    this._recount();
    // online guest: tell the host what our own people like their bots to do
    if (this.mirror) for (const p of this._people()) if (this._isLocal(p)) net.sendPrefs(p.dev, this.prefsOf(this.ownerKey(p)));
    this._setupCamera();
    this.glowLights = [];
    for (let i = 0; i < 8; i++) {
      const l = new Light({ position: [0, -10, 0], intensity: 0, range: 2.4 });
      this.scene.lights.push(l);
      this.glowLights.push(l);
    }
    this.baseLights = this.scene.lights.map((l) => l.intensity);
    this.coachOn = app.save.settings.coach !== false;
    this._coachT = 0;
    if (net && !this.mirror) {
      // host: one-off effects are recorded here and replayed on every guest's screen
      this.hud = recordingHud(app.hud, this);
      this._rec = true;
      this.scene.particles.forEach((ps, k) => {
        const emit = ps.emit.bind(ps);
        ps.emit = (o) => {
          emit(o);
          if (this._rec) this._ev('p', [k, packEmit(o)]);
        };
      });
    }
    if (this.mirror) {
      // a guest's kitchen starts empty: every item arrives in the host's snapshots
      for (const s of this.stations) if (s.item) s.takeItem().dispose();
      this.vessels = [];
    }
    this.hud.startLevel(this);
    this._onResize = () => this._setupCamera();
    window.addEventListener('resize', this._onResize);
  }

  /** Target selector (always visible), button preview and reactive coach hints. */
  _updateCoach(dt, t) {
    const playing = this.state === 'play';
    this._coachT -= dt;
    const refreshHints = this._coachT <= 0;
    if (refreshHints) this._coachT = 0.25;
    for (const c of this.chefs) {
      const sel = c.selector;
      const show = playing && c.active && c.respawn <= 0 && c.falling <= 0 && !c.isBot;
      if (!show) {
        sel.visible = false;
        c.preview = null;
        c.hint = null;
        continue;
      }
      let tg = c.target;
      // the belt item it aimed at may have slid onto the next tile this frame: aim again
      if (tg && tg.station && tg.station.kind === 'conveyor' && !tg.station.item && !c.held) tg = c.target = this.findTarget(c);
      let pos = null, sc = 1;
      const belt = tg && tg.station && this._beltSpot(tg.station);
      if (belt) {
        // frame the belt item itself, not its tile
        pos = [belt[0], tg.station.node.position[1] + 0.06, belt[1]];
        sc = 0.5;
      } else if (tg && tg.station) {
        const s = tg.station;
        pos = [s.wx, s.node.position[1] + 0.012, s.wz];
      } else if (tg && tg.chef) {
        pos = [tg.chef.x, 0.045, tg.chef.z];
        sc = 0.75;
      } else if (tg && tg.floor) {
        pos = [tg.floor.x, 0.045, tg.floor.z];
        sc = 0.55;
      } else if (c.held && !this._facingSolid(c)) {
        pos = [c.x + c.fx * 0.45, 0.045, c.z + c.fz * 0.45];
        sc = 0.45;
      }
      c.preview = c.local ? previewAction(this, c, tg) : null;
      if (pos) {
        sel.visible = true;
        const pulse = 1 + Math.sin(t * 6 + c.index) * 0.03;
        sel.position = pos;
        sel.scale = [sc * pulse, 1, sc * pulse];
        const colr = c.preview && c.preview.bad ? [1, 0.25, 0.25] : col(c.color);
        const k = 2.2 + Math.sin(t * 6) * 0.4;
        sel.tint = [colr[0] * k, colr[1] * k, colr[2] * k, 1];
        c.selPos = pos;
      } else {
        sel.visible = false;
        c.selPos = null;
      }
      if (refreshHints) c.hint = this.coachOn && c.local ? coachHint(this, c) : null;
    }
  }

  // ------------------------------------------------------------ setup
  _setupScene() {
    const th = this.theme, s = this.scene, e = s.env;
    s.sky.neb1 = col(th.neb1);
    s.sky.neb2 = col(th.neb2);
    s.sky.neb3 = col(th.neb3);
    s.sky.planetCol = col(th.planet);
    s.sky.planetCol2 = col(th.planet2);
    s.sky.seed = th.seed;
    s.sky.planetDir = [-0.35, 0.3, -1];
    s.sky.planetSize = 0.2;
    s.sky.starDir = th.sun ? [0.5, 0.35, -1] : [0.7, 0.5, -0.6];
    s.sky.starCol = th.sun ? col('#ffd08a').map((v) => v * 2) : col('#ffe0c0');
    e.sunDir = [-0.45, -1, -0.55];
    e.sunColor = col('#ffeedd');
    e.sunIntensity = 1.9;
    e.sunColor = col('#ffe2c4');
    e.skyAmb = col(th.neb1).map((v) => v * 0.6 + 0.08);
    e.groundAmb = col(th.floor).map((v) => v * 0.5 + 0.02);
    e.rim = col(th.accent2);
    e.rimIntensity = 0.35;
    e.fogColor = col('#05040f');
    e.fogDensity = 0.0;
    e.exposure = 1.05;
    e.bloom = 1.0;
    e.bloomThreshold = 1.1;
    e.vignette = 0.38;
    e.envIntensity = 0.9;
    e.saturation = 1.08;
    e.lift = [0.01, 0.0, 0.03];
    e.gain = [1.02, 1.0, 1.0];
    this.baseEnv = { sun: e.sunIntensity, sky: e.skyAmb.slice(), ground: e.groundAmb.slice(), env: e.envIntensity };
  }
  tileToWorld(x, y) {
    return [x - this.W / 2 + 0.5, y - this.H / 2 + 0.5];
  }
  worldToTile(wx, wz) {
    return [Math.floor(wx + this.W / 2), Math.floor(wz + this.H / 2)];
  }
  _parseMap() {
    const map = this.level.map;
    this.H = map.length;
    this.W = map[0].length;
    this.grid = [];
    this.stations = [];
    this.spawns = {};
    for (let y = 0; y < this.H; y++) {
      const row = [];
      for (let x = 0; x < this.W; x++) {
        const c = map[y][x];
        let st = null;
        const kind = stationKind(c, this.level.crates);
        if (kind && kind !== 'wall') {
          st = new Station(this, x, y, c, kind);
          this.stations.push(st);
        }
        if ('1234'.includes(c)) this.spawns[c] = this.tileToWorld(x, y);
        row.push({ ch: c, st });
      }
      this.grid.push(row);
    }
    this.racks = this.stations.filter((s) => s.kind === 'rack');
    const cups = this.level.cups;
    const counts = this.racks.map(() => 0);
    for (let i = 0; i < cups; i++) counts[i % counts.length]++;
    this.racks.forEach((r, i) => r.setRackCount(counts[i]));
    this.sinks = this.stations.filter((s) => s.kind === 'sink');
    this.sinks.forEach((s) => s.setSinkCount(0));
  }
  registerVessel(v, station) {
    v.home = station;
    this.vessels.push(v);
    return v;
  }
  _spawnChefs() {
    this.chefs = [];
    const n = this.players.length;
    const sp = [this.spawns['1'], this.spawns['2'], this.spawns['3'], this.spawns['4']];
    for (let i = 0; i < n; i++) {
      let p = sp[i];
      if (!p) {
        const base = sp[i % 2] || sp[0];
        p = this._nearestFree(base[0] + (i >= 2 ? 1 : 0), base[1] + (i >= 2 ? 1 : 0));
      }
      const c = this._makeChef(i, p);
      const pl = this.players[i];
      c.controller = pl.device;
      c.peer = pl.peer || null;
      c.isBot = isBot(pl);
      this._bindChef(c);
      c.active = true;
      // the host (or an offline game) runs the bots; a guest just sees them
      if (c.isBot && !this.mirror) this.helpers.push(new Helper(this, c));
    }
  }
  _makeChef(i, p) {
    const c = new Chef(this, i, p[0], p[1]);
    c.selector = new Node(M.selectorMesh(), { fx: { mode: 0 }, castShadow: false, reflect: false });
    c.selector.visible = false;
    this.scene.add(c.selector);
    c.hint = null;
    c.preview = null;
    this.chefs.push(c);
    return c;
  }
  /** Online: is this chef played on this screen (local) or somewhere else (remote)? */
  _bindChef(c) {
    c.local = !this.net || c.peer === this.net.peer;
    c.remote = !c.local;
    // a guest's own chef takes the host's position once, from the first snapshot
    c.warp = this.mirror && c.local ? -1 : 0;
    c.netLock = false;
  }
  /** Host: someone joined mid-shift. They get a chef of their own, or take over a bot's when the kitchen is full. */
  seatPeer(peer, dev) {
    const player = { peer, dev, device: `net:${peer}:${dev}` };
    let chef;
    if (this.chefs.length < 4) {
      const base = this.spawns[String(this.chefs.length + 1)] || this.spawns['1'];
      chef = this._makeChef(this.chefs.length, this._nearestFree(base[0], base[1]));
      this.players.push(player);
    } else {
      chef = [...this.chefs].reverse().find((c) => c.isBot);
      if (!chef) return false;
      this.players[chef.index] = player;
      this.dropHelper(chef);
    }
    chef.controller = player.device;
    chef.peer = peer;
    chef.isBot = false;
    this._bindChef(chef);
    chef.active = true;
    this._recount(); // a new person gets their share of the bots
    return true;
  }
  /** Host: someone joined mid-shift with no chef left to take (four people already cook). They watch, and get their share of the bots. */
  addSpectator(peer, dev) {
    this.spectators.push({ peer, dev, device: `net:${peer}:${dev}` });
    this._recount();
  }
  // ------------------------------------------------------------ who owns which bot
  /** A person's key: their device offline; online, their browser and the device on it. */
  ownerKey(p) {
    return p.peer ? p.peer + ':' + p.dev : p.device;
  }
  _isLocal(p) {
    return !this.net || p.peer === this.net.peer;
  }
  /** The people here, cooking or watching: the host's first, then the cooks in crew slot order, then the spectators. */
  _people() {
    const host = this.net && this.net.hostPeer;
    const rank = (p) => (p.peer && p.peer !== host ? 1 : 0);
    // (sorting is stable, so the cooks and the spectators each keep their own order)
    return [...this.players.filter((p) => !isBot(p)), ...this.spectators].sort((a, b) => rank(a) - rank(b));
  }
  /** Work out whether one person plays solo, and share the bots out afresh (as a shift starts, and as each person arrives). */
  _recount() {
    this.solo = this._people().length === 1;
    this._assignBots();
  }
  /** Share the bots out as evenly as can be: the first to the host, the next to the next person, and round again. */
  _assignBots() {
    const people = this._people();
    this.botOwner = {};
    let k = 0;
    this.players.forEach((p, i) => {
      if (isBot(p) && people.length) this.botOwner[i] = this.ownerKey(people[k++ % people.length]);
    });
  }
  /** The bots this person owns. */
  ownedBots(key) {
    return this.chefs.filter((c) => c.isBot && this.botOwner[c.index] === key);
  }
  /** Where this browser keeps a person's choices: its first player's as 'main' (whoever usually plays here), others by device. */
  _prefKey(key) {
    const locals = this._people().filter((p) => this._isLocal(p));
    const i = locals.findIndex((p) => this.ownerKey(p) === key);
    if (i < 0) return null;
    return i === 0 ? 'main' : locals[i].dev || locals[i].device;
  }
  /** A person's Delegate choices (every task on until they switch it off). */
  prefsOf(key) {
    let d = key && this.delegateBy[key];
    if (!d) {
      const pk = key && this._prefKey(key);
      const saved = this.app.save.delegateBy;
      if (pk) d = saved[pk] = saved[pk] || {};
      else d = { ...((this.net && this.net.prefs && this.net.prefs[key]) || {}) }; // (a guest's, as they sent them)
      if (key) this.delegateBy[key] = d;
    }
    for (const t of DELEGATE_TASKS) if (d[t.id] === undefined) d[t.id] = true;
    return d;
  }
  /** What this bot's owner has it doing. */
  botPrefs(chef) {
    return this.prefsOf(this.botOwner[chef.index]);
  }
  /** A bot's chef goes back to being an ordinary chef (a person took it over). */
  dropHelper(chef) {
    const h = this.helpers.find((x) => x.chef === chef);
    if (!h) return;
    h._stop();
    this.helpers = this.helpers.filter((x) => x !== h);
    chef.bot = null;
    chef.thought = '';
  }
  /** Guest: the host's chefs changed hands (someone joined mid-shift). */
  mirrorCrew(peers, chefs) {
    peers.forEach((peer, i) => {
      const c = this.chefs[i] || this._makeChef(i, [chefs[i][0], chefs[i][1]]);
      if (c.peer === peer && c.local !== undefined) return;
      c.peer = peer;
      c.isBot = false; // bots only join in the lobby: a new face here is a person
      this._bindChef(c);
      const mine = this.net.crew.find((q) => q.peer === peer);
      c.controller = c.local ? (mine ? mine.dev : 'any') : 'net';
    });
  }
  /** Host: where a guest says their own chefs are (they move on their own screen, straight away). */
  applyReports(peer, list) {
    for (const [i, x, z, face, vx, vz, dashT, warp] of list) {
      const c = this.chefs[i];
      // a report from before the host last moved this chef itself (teleport, fall, respawn) is stale
      if (!c || c.peer !== peer || !c.remote || warp !== c.warp || c.falling > 0 || c.respawn > 0) continue;
      if (dashT > 0.1 && c.dashT <= 0) this._dashFx(c, peer);
      [c.x, c.z] = this.pushOut(x, z, 0.3);
      c.face = face;
      c.vx = vx;
      c.vz = vz;
      c.dashT = dashT;
    }
  }
  /** A controller for a player's device: this browser's own, or a guest's (online host). */
  readCtrl(dev) {
    if (dev.startsWith('bot')) {
      const h = this.helpers.find((x) => x.chef.controller === dev);
      return h ? h.ctrl : null;
    }
    if (dev.startsWith('net:')) return this.net.ctrlFor(dev);
    if (dev === 'any') return input.readAny([...this.players, ...this.spectators].map((p) => p.device).filter((d) => d !== 'any' && !d.startsWith('net:')));
    return input.read(dev);
  }
  _nearestFree(wx, wz) {
    const [tx, ty] = this.worldToTile(wx, wz);
    for (let r = 0; r < 5; r++)
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          const c = this.tileAt(tx + dx, ty + dy);
          if (c && '.'.includes(c.ch)) return this.tileToWorld(tx + dx, ty + dy);
        }
    return [wx, wz];
  }
  _setupCamera() {
    const cam = this.camera;
    cam.fov = (34 * Math.PI) / 180;
    // view.rows frames the level as if it had that many rows; view.shift slides it up the screen
    const view = this.level.view || {};
    const [cx, cz] = [0, -0.3 + (view.shift || 0)];
    const W = this.W + 1.5, H = (view.rows || this.H) + 4.2;
    const aspect = Math.max(1.2, window.innerWidth / window.innerHeight);
    const tanV = Math.tan(cam.fov / 2), tanH = tanV * aspect;
    const pitch = (56 * Math.PI) / 180;
    const distW = W / 2 / tanH;
    const distH = (H / 2) * Math.sin(pitch) / tanV + H * 0.12;
    const dist = Math.max(distW, distH) * 1.02;
    // how far the view may drift sideways after its chefs before a wide kitchen's edge leaves the screen
    const slackX = Math.min(0.6, Math.max(0, dist * tanH - W / 2));
    this.camBase = { target: [cx, 0.2, cz - 0.55], dist, pitch, slackX };
    this._placeCamera(0);
  }
  _placeCamera(dt) {
    if (this._camLock) return; // debug: frozen camera for close-up captures
    const b = this.camBase, cam = this.camera;
    let ox = 0, oz = 0;
    if (this.chefs && this.chefs.length) {
      // online, each screen follows its own chefs (someone else teleporting doesn't swing your view)
      const alive = this.chefs.filter((c) => c.active && c.respawn <= 0);
      const mine = alive.filter((c) => c.local && !c.isBot);
      const act = mine.length ? mine : alive;
      if (act.length) {
        for (const c of act) { ox += c.x; oz += c.z; }
        ox /= act.length; oz /= act.length;
      }
    }
    const tx = b.target[0] + clamp(ox * 0.08, -b.slackX, b.slackX), tz = b.target[2] + clamp(oz * 0.06, -0.4, 0.4);
    const introK = this.state === 'intro' ? 1 - Math.pow(clamp(this.stateT / 2.4, 0, 1), 3) * 1 : 0;
    const d = b.dist * (1 + introK * 0.5);
    const yaw = introK * 0.5;
    const sh = this.shake > 0 ? this.shake : 0;
    const sx = (Math.random() - 0.5) * sh * 0.3, sy = (Math.random() - 0.5) * sh * 0.3;
    const p = b.pitch + introK * 0.15;
    const want = [tx + Math.sin(yaw) * Math.cos(p) * d + sx, Math.sin(p) * d + sy, tz + Math.cos(yaw) * Math.cos(p) * d];
    if (dt === 0) cam.position = want;
    else cam.position = cam.position.map((v, i) => lerp(v, want[i], 1 - Math.exp(-4 * dt)));
    cam.target = [tx, b.target[1], tz];
  }

  // ------------------------------------------------------------ helpers
  tileAt(x, y) {
    if (y < 0 || y >= this.H || x < 0 || x >= this.W) return null;
    return this.grid[y][x];
  }
  walkable(tx, ty) {
    const t = this.tileAt(tx, ty);
    if (!t) return false;
    if (t.ch === '=') return this.bridgeOpen(tx, ty);
    return WALK.includes(t.ch);
  }
  bridgeOpen(tx, ty) {
    const b = this.world.bridges.find((b) => b.x === tx && b.y === ty);
    return b ? b.ext > 0.95 : false;
  }
  solidCircle(wx, wz, r) {
    const x0 = Math.floor(wx - r + this.W / 2), x1 = Math.floor(wx + r + this.W / 2);
    const y0 = Math.floor(wz - r + this.H / 2), y1 = Math.floor(wz + r + this.H / 2);
    for (let y = y0; y <= y1; y++)
      for (let x = x0; x <= x1; x++) {
        const t = this.tileAt(x, y);
        let solid = !t || !WALK.includes(t.ch);
        if (t && t.ch === '=') solid = false; // falling is handled separately
        if (!solid) continue;
        // circle vs tile AABB
        const [cx, cz] = this.tileToWorld(x, y);
        const nx = clamp(wx, cx - 0.5, cx + 0.5), nz = clamp(wz, cz - 0.5, cz + 0.5);
        if ((wx - nx) ** 2 + (wz - nz) ** 2 < r * r) return true;
      }
    return false;
  }
  /** Push a circle out of solid tiles. Round corners deflect it, so chefs slide around them. */
  pushOut(wx, wz, r) {
    for (let it = 0; it < 4; it++) {
      let hit = false;
      const x0 = Math.floor(wx - r + this.W / 2), x1 = Math.floor(wx + r + this.W / 2);
      const y0 = Math.floor(wz - r + this.H / 2), y1 = Math.floor(wz + r + this.H / 2);
      for (let y = y0; y <= y1; y++)
        for (let x = x0; x <= x1; x++) {
          const t = this.tileAt(x, y);
          let solid = !t || !WALK.includes(t.ch);
          if (t && t.ch === '=') solid = false;
          if (!solid) continue;
          const [cx, cz] = this.tileToWorld(x, y);
          const nx = clamp(wx, cx - 0.5, cx + 0.5), nz = clamp(wz, cz - 0.5, cz + 0.5);
          const dx = wx - nx, dz = wz - nz, d2 = dx * dx + dz * dz;
          if (d2 >= r * r) continue;
          hit = true;
          if (d2 > 1e-9) {
            const d = Math.sqrt(d2), k = (r - d) / d;
            wx += dx * k;
            wz += dz * k;
          } else {
            // centre inside the tile: leave along the shallowest side
            const px = 0.5 - Math.abs(wx - cx), pz = 0.5 - Math.abs(wz - cz);
            if (px < pz) wx += Math.sign(wx - cx || 1) * (px + r);
            else wz += Math.sign(wz - cz || 1) * (pz + r);
          }
        }
      if (!hit) break;
    }
    return [wx, wz];
  }
  perkActive(n) {
    return (this.perks[n] || 0) > 0;
  }
  sfx(n, o) {
    audio.sfx(n, o);
    this._ev('s', o ? [n, o] : [n]);
  }
  /** Host: queue a one-off effect for the guests' screens (see src/net/sync.js). */
  _ev(type, args) {
    if (this.net && !this.mirror) this.netEvents.push(this._skipPeer ? [type, args, this._skipPeer] : [type, args]);
  }
  takeEvents() {
    const e = this.netEvents;
    this.netEvents = [];
    return e;
  }
  /** A dash's whoosh and dust puff. skip: the guest whose own screen already showed it. */
  _dashFx(chef, skip = null) {
    this._skipPeer = skip;
    this.sfx('dash');
    for (let i = 0; i < 10; i++)
      this.scene.particles[0].emit({ pos: [chef.x, 0.3, chef.z], vel: [(Math.random() - 0.5) * 2 - chef.fx * 2, Math.random(), (Math.random() - 0.5) * 2 - chef.fz * 2], life: 0.5, size: 0.08, size1: 0.01, color: col(chef.color), alpha: 1.2, drag: 3 });
    this._skipPeer = null;
  }
  /** Extinguisher foam from a chef's nozzle (one frame's worth). */
  _spray(chef) {
    const px = chef.x + chef.fx * 0.5, pz = chef.z + chef.fz * 0.5;
    for (let i = 0; i < 3; i++)
      this.scene.particles[1].emit({ pos: [px, 0.9, pz], vel: [chef.fx * rand(3, 5) + rand(-0.6, 0.6), rand(-0.2, 0.6), chef.fz * rand(3, 5) + rand(-0.6, 0.6)], life: 0.5, size: 0.08, size1: 0.3, color: [0.85, 0.95, 1], alpha: 0.8, drag: 3, type: 2 });
  }

  // ------------------------------------------------------------ targeting
  findTarget(chef) {
    const [tx, ty] = this.worldToTile(chef.x, chef.z);
    const fx = chef.fx, fz = chef.fz;
    let best = null, bestS = -1e9;
    // floor items first (in front, close)
    for (const f of this.floorItems) {
      const dx = f.x - chef.x, dz = f.z - chef.z;
      const d = Math.hypot(dx, dz);
      if (d > 0.95) continue;
      const dot = d < 0.2 ? 1 : (dx * fx + dz * fz) / d;
      if (dot < 0.3) continue;
      const s = dot * 1.2 - d * 0.8 + 0.2;
      if (s > bestS) { bestS = s; best = { floor: f }; }
    }
    // a teammate with empty hands right in front of you: hand them what you hold (bots never hand things over)
    if (chef.held && !chef.isBot) {
      for (const o of this.chefs) {
        if (o === chef || o.held || o.respawn > 0 || o.falling > 0) continue;
        const dx = o.x - chef.x, dz = o.z - chef.z, d = Math.hypot(dx, dz);
        if (d > 1.25 || d < 1e-3) continue;
        const dot = (dx * fx + dz * fz) / d;
        if (dot < 0.55) continue;
        const s = dot * 2 - d * 0.6 + 0.6;
        if (s > bestS) { bestS = s; best = { chef: o }; }
      }
    }
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const t = this.tileAt(tx + dx, ty + dy);
        if (!t || !t.st) continue;
        // empty-handed, a bare stretch of belt has nothing to grab: aim at the items riding it instead
        if (!chef.held && t.st.kind === 'conveyor' && !t.st.item) continue;
        let [cx, cz] = this.tileToWorld(tx + dx, ty + dy);
        // on a belt, aim at where the item actually is: it slides from tile to tile
        const belt = this._beltSpot(t.st);
        if (belt) [cx, cz] = belt;
        const vx = cx - chef.x, vz = cz - chef.z;
        const d = Math.hypot(vx, vz);
        if (d > 1.35) continue;
        const dot = (vx * fx + vz * fz) / d;
        if (dot < 0.35) continue;
        const s = dot * 1.5 - d * 0.9;
        if (s > bestS) { bestS = s; best = { station: t.st }; }
      }
    return best;
  }

  // ------------------------------------------------------------ interaction
  grab(chef) {
    const tgt = this.findTarget(chef);
    const H = chef.held;
    if (!H) {
      if (!tgt) return;
      if (tgt.floor) {
        this.floorItems.splice(this.floorItems.indexOf(tgt.floor), 1);
        this.scene.root.remove(tgt.floor.item.node);
        chef.hold(tgt.floor.item);
        this.sfx('pickup');
        return;
      }
      const s = tgt.station;
      if (s.fire) return this._nope(chef, 'Fire!');
      if (s.kind === 'crate') {
        if (s.item) chef.hold(s.takeItem());
        else chef.hold(new Ingredient(s.ingId));
        s.highlight = 1.5;
        this.sfx('pickup');
      } else if (s.kind === 'rack') {
        if (s.item) chef.hold(s.takeItem());
        else if (s.clean > 0) {
          s.setRackCount(s.clean - 1);
          chef.hold(new Cup());
          this.sfx('pickup');
        } else this._nope(chef, 'No clean cups!');
      } else if (s.kind === 'cups' || s.kind === 'straws') {
        chef.hold(s.kind === 'cups' ? new BobaCup() : new Straw());
        s.highlight = 1.5;
        this.sfx('pickup');
      } else if (s.kind === 'sink') {
        return;
      } else if (s.item) {
        chef.hold(s.takeItem());
        this.sfx('pickup');
      }
      return;
    }
    // holding something
    if (tgt && tgt.chef) {
      const o = tgt.chef;
      o.hold(chef.release());
      o.squash = 1;
      this.sfx('pickup');
      this.hud.popText(`→ ${o.name}`, [o.x, 1.9, o.z], o.color);
      return;
    }
    if (!tgt || tgt.floor) {
      if (!tgt && this._facingSolid(chef)) return;
      this.dropToFloor(chef);
      return;
    }
    const s = tgt.station;
    if (s.fire) return this._nope(chef, 'Fire!');
    // holding dirty cups + more dirty cups here: scoop them onto your stack
    if (H.kind === 'cup' && H.dirty && s.item && s.item.kind === 'cup' && s.item.dirty) {
      H.addStack(s.takeItem());
      chef.squash = 0.6;
      this.sfx('pickup');
      return;
    }
    switch (s.kind) {
      case 'bin':
        return this._binItem(chef, H, s);
      case 'bar':
        return this._serve(chef, H, s);
      case 'sink':
        if (H.kind === 'cup' && H.dirty) {
          s.setSinkCount(s.dirty + H.count);
          chef.release().dispose();
          this.sfx('place');
          // start washing right away (stay at the sink to keep going)
          if (this._jobValid(chef, s)) chef.job = s;
        } else this._nope(chef, 'Only dirty cups');
        return;
      case 'rack':
        if (H.kind === 'bobacup') return this._nope(chef, 'Boba cups are disposable');
        if (H.kind === 'cup' && H.isCleanEmpty) {
          s.setRackCount(s.clean + 1);
          chef.release().dispose();
          this.sfx('place');
        } else if (!s.item && H.kind !== 'cup') {
          s.setItem(chef.release());
          this.sfx('place');
        }
        return;
      case 'crate':
        if (H.kind === 'bobacup' && s.ingId === 'pearls') return this._pearlsInto(chef, H, null, s);
        if (H.kind === 'ing' && H.id === s.ingId && H.state === 'raw') {
          chef.release().dispose();
          this.sfx('place');
        } else if (!s.item) {
          s.setItem(chef.release());
          this.sfx('place');
        } else this._combine(chef, H, s);
        return;
      case 'chute':
        return;
      case 'cups':
        if (H.kind === 'bobacup' && !H.pearls && !H.drink) {
          chef.release().dispose();
          this.sfx('place');
        } else this._nope(chef, H.kind === 'bobacup' ? 'Already used: finish it or zap it' : 'Boba cups only');
        return;
      case 'straws':
        if (H.kind === 'straw') {
          chef.release().dispose();
          this.sfx('place');
        } else if (H.kind === 'bobacup') this._strawInto(chef, H, null);
        else this._nope(chef, 'Straws only');
        return;
      case 'sealer':
        if (!s.item && H.kind === 'bobacup' && !H.sealable) return this._nope(chef, this._whyNotSealable(H));
        break;
    }
    if (s.item) this._combine(chef, H, s);
    else if (s.accepts(H)) {
      s.setItem(chef.release());
      if (s.kind === 'conveyor') s.beltT = 0.5;
      this.sfx('place');
      // dropping something on a board starts prepping it right away (stay put to keep going)
      if ((s.kind === 'chop' || s.kind === 'grind') && this._jobValid(chef, s)) chef.job = s;
    } else {
      const why = s.kind === 'stove' ? 'Kettles only' : s.kind === 'blender' ? 'Blender jugs only' : s.kind === 'carbonator' ? (H.kind === 'bobacup' ? 'Boba doesn’t fizz' : 'Filled cups only') : s.kind === 'sealer' ? 'Boba cups only' : 'Can’t place';
      this._nope(chef, why);
    }
  }
  _facingSolid(chef) {
    const px = chef.x + chef.fx * 0.6, pz = chef.z + chef.fz * 0.6;
    return this.solidCircle(px, pz, 0.05);
  }
  _sludgePop(st) {
    this.sfx('wrong');
    this.hud.popText('Murky Sludge! No drink can come from this mix &mdash; empty it at the Zapper', [st.wx, 2.1, st.wz], '#c8d48a', true);
  }
  _nope(chef, text) {
    this.sfx('wrong');
    this.hud.popText(text, [chef.x, 1.9, chef.z], '#ff9a9a');
  }
  /** Why this boba cup can't go in the Sealer yet. */
  _whyNotSealable(cup) {
    if (cup.sealed) return 'Already sealed';
    if (!cup.pearls && !cup.drink) return 'Add pearls and tea first';
    if (!cup.pearls) return 'Add boba pearls first';
    return 'Pour the tea in first';
  }
  /** Scoop pearls into a boba cup. used: an Ingredient that gets used up (null = straight from the crate). */
  _pearlsInto(chef, cup, used, at) {
    if (cup.sealed) return this._nope(chef, 'Already sealed');
    if (cup.pearls) return this._nope(chef, 'Already has pearls');
    cup.addPearls();
    if (used) used.dispose();
    this.sfx('addIng');
    this._splash([at.wx, COUNTER_H - 0.3, at.wz], INGREDIENTS.pearls.accent);
    return true;
  }
  /** Stab a straw through a sealed boba cup. used: a Straw that gets used up (null = from the holder). */
  _strawInto(chef, cup, used) {
    if (cup.straw) return this._nope(chef, 'Already has a straw');
    if (!cup.sealed) return this._nope(chef, cup.sealable ? 'Seal it first (Sealer)' : this._whyNotSealable(cup));
    cup.addStraw();
    if (used) used.dispose();
    this.sfx('straw');
    return true;
  }
  /** Pour a finished kettle or blender into a boba cup. */
  _pourBoba(chef, cup, v, st) {
    if (!v.result) return this._nope(chef, v.contents.length ? 'Not ready yet' : 'Empty');
    if (v.result === 'sludge') return this._nope(chef, 'It’s sludge! Empty it at the Zapper');
    if (!RECIPES[v.result].boba) return this._nope(chef, 'Boba cups are for boba tea');
    if (cup.sealed) return this._nope(chef, 'Already sealed');
    if (cup.drink) return this._nope(chef, 'Cup is full');
    cup.fill(v.result);
    v.reset();
    this.sfx('pour');
    this._splash(st.node.position, RECIPES[cup.drink].color);
  }
  _combine(chef, H, s) {
    const X = s.item;
    // --- boba
    if (H.kind === 'ing' && H.id === 'pearls' && X.kind === 'bobacup') {
      if (X.canTakePearls) this._pearlsInto(chef, X, chef.release(), s);
      else this._pearlsInto(chef, X, null, s);
      return;
    }
    if (H.kind === 'bobacup' && X.kind === 'ing' && X.id === 'pearls') {
      if (H.canTakePearls) this._pearlsInto(chef, H, s.takeItem(), s);
      else this._pearlsInto(chef, H, null, s);
      return;
    }
    if (H.kind === 'straw' && X.kind === 'bobacup') {
      if (X.sealed && !X.straw) this._strawInto(chef, X, chef.release());
      else this._strawInto(chef, X, null);
      return;
    }
    if (H.kind === 'bobacup' && X.kind === 'straw') {
      if (H.sealed && !H.straw) this._strawInto(chef, H, s.takeItem());
      else this._strawInto(chef, H, null);
      return;
    }
    if (H.kind === 'bobacup' && X.kind === 'vessel') return this._pourBoba(chef, H, X, s);
    if (H.kind === 'vessel' && X.kind === 'bobacup') return this._pourBoba(chef, X, H, s);
    if ((H.kind === 'ing' && H.id === 'pearls' && X.kind === 'vessel') || (H.kind === 'vessel' && X.kind === 'ing' && X.id === 'pearls')) return this._nope(chef, 'Pearls go in a boba cup');
    const bobaIn = (v) => v.kind === 'vessel' && RECIPES[v.result] && RECIPES[v.result].boba;
    if ((H.kind === 'cup' && bobaIn(X)) || (X.kind === 'cup' && bobaIn(H))) return this._nope(chef, 'Boba tea needs a boba cup');
    if (H.kind === 'ing' && X.kind === 'vessel') {
      if (!H.ready) return this._nope(chef, INGREDIENTS[H.id].prep === 'chop' ? 'Chop it first!' : 'Grind it first!');
      if (!X.canAdd(H)) return this._nope(chef, X.burnt ? 'Burnt! Zap it' : X.result === 'sludge' ? 'It’s sludge! Empty it at the Zapper' : 'Full!');
      if (X.add(chef.release()) === 'sludge') this._sludgePop(s);
      this._splash(s.node.position, INGREDIENTS[H.id].color);
      H.dispose();
      this.sfx('addIng');
      return;
    }
    if (H.kind === 'vessel' && X.kind === 'ing') {
      if (!X.ready) return this._nope(chef, INGREDIENTS[X.id].prep === 'chop' ? 'Chop it first!' : 'Grind it first!');
      if (!H.canAdd(X)) return this._nope(chef, H.burnt ? 'Burnt! Zap it' : H.result === 'sludge' ? 'It’s sludge! Empty it at the Zapper' : 'Full!');
      const it = s.takeItem();
      if (H.add(it) === 'sludge') this._sludgePop({ wx: chef.x, wz: chef.z });
      it.dispose();
      this.sfx('addIng');
      return;
    }
    if (H.kind === 'cup' && X.kind === 'vessel') {
      if (!H.isCleanEmpty) return this._nope(chef, H.dirty ? 'Wash the cup first' : 'Cup is full');
      if (!X.result) return this._nope(chef, X.contents.length ? 'Not ready yet' : 'Empty');
      H.fill(X.result);
      X.reset();
      this.sfx('pour');
      this._splash(s.node.position, (RECIPES[H.drink] || SLUDGE).color);
      return;
    }
    if (H.kind === 'vessel' && X.kind === 'cup') {
      if (!X.isCleanEmpty) return this._nope(chef, X.dirty ? 'Wash the cup first' : 'Cup is full');
      if (!H.result) return this._nope(chef, H.contents.length ? 'Not ready yet' : 'Empty');
      X.fill(H.result);
      H.reset();
      this.sfx('pour');
      return;
    }
    if (H.kind === 'cup' && X.kind === 'cup' && H.dirty && X.dirty) {
      X.addStack(chef.release());
      this.sfx('place');
      return;
    }
    this._nope(chef, 'Occupied');
  }
  /** True while the chef is still standing where they just emptied something into this recycler. */
  justDumped(chef, s) {
    const d = chef._dumped;
    return !!(d && d.s === s && Math.hypot(chef.x - d.x, chef.z - d.z) < 0.45);
  }
  _binItem(chef, H, s) {
    const emptyNow = (H.kind === 'cup' && !H.drink) || (H.kind === 'vessel' && !H.contents.length && !H.burnt);
    if (emptyNow && this.justDumped(chef, s)) return; // just emptied it here: nothing to complain about
    if (H.kind === 'ing' || H.kind === 'bobacup' || H.kind === 'straw') {
      chef.release().dispose();
    } else if (H.kind === 'cup') {
      if (H.drink) H.empty();
      else return this._nope(chef, 'Cups are reusable!');
    } else if (H.kind === 'vessel') {
      if (!H.contents.length && !H.burnt) return;
      H.reset();
    } else return;
    chef._dumped = { s, x: chef.x, z: chef.z };
    if (s.zap) s.zap();
    else this.sfx('drop');
    const [x, , z] = s.node.position;
    for (let i = 0; i < 22; i++) this.scene.particles[0].emit({ pos: [x, 1.05, z], vel: [rand(-1.4, 1.4), rand(1.2, 3), rand(-1.4, 1.4)], life: 0.5, size: 0.05, size1: 0, color: col('#bfeaff'), color1: [1, 1, 1], alpha: 2, gravity: 4, type: 1 });
  }
  _serve(chef, H, s) {
    const seatIdx = this.world.seats.indexOf(s.x);
    const guest = this.guests.find((g) => g.seat === seatIdx && g.state === 'wait');
    const boba = H.kind === 'bobacup';
    if ((H.kind !== 'cup' && !boba) || !H.drink) {
      if (!s.item && H.kind === 'cup' && H.dirty) return this._nope(chef, 'Take dirty cups to the sink');
      return this._nope(chef, guest ? 'Serve a drink in a cup!' : 'No guest here');
    }
    if (!guest) return this._nope(chef, 'No guest here');
    const r = guest.recipe;
    if (boba && H.drink === r.id) {
      // the right tea: every boba step has to be done before it goes over the bar
      if (!H.pearls) return this._nope(chef, 'Needs boba pearls!');
      if (!H.sealed) return this._nope(chef, 'Seal it first!');
      if (!H.straw) return this._nope(chef, 'Needs a straw!');
    }
    if (H.drink !== r.id || !!H.fizzy !== !!r.fizz) {
      guest.refuse();
      const msg = H.drink === 'sludge' ? 'Yuck! Sludge!' : H.drink === r.id && r.fizz && !H.fizzy ? 'Needs fizz!' : 'Wrong drink!';
      return this._nope(chef, msg);
    }
    chef.release();
    guest.serve(H);
    this._scoreServe(guest);
  }
  _scoreServe(guest) {
    const r = guest.recipe;
    const base = 20 + r.ing.length * 6 + (r.fizz ? 10 : 0) + (r.boba ? 16 : 0);
    this.streak++;
    const mul = this.streakMul();
    let tip = Math.round(base * 0.45 * guest.frac * guest.species.tip * mul);
    if (this.perkActive('tips')) tip *= 2;
    this.coins += base + tip;
    this.tips += tip;
    this.served++;
    this.sfx('serve');
    setTimeout(() => this.sfx('coin'), 180);
    const p = [guest.x, 2.0, guest.z];
    this.hud.popText(`+${base}` + (tip ? ` <span class="tip">+${tip} tip</span>` : ''), p, '#ffe27a', true);
    this.hud.bumpScore(mul);
    // coins burst
    for (let i = 0; i < 24; i++)
      this.scene.particles[0].emit({ pos: [guest.x, 1.2, guest.z], vel: [rand(-1.5, 1.5), rand(2, 4), rand(-1, 1.5)], life: 1.1, size: 0.07, size1: 0.02, color: col('#ffd35a'), color1: col('#ff9a3a'), gravity: 6, type: 1, spin: 4 });
    if (r.perk) this.applyPerk(r.perk, r.id, guest);
    this.hud.orderDone(guest, true);
  }
  streakMul() {
    return Math.min(4, 1 + Math.floor(this.streak / 2));
  }
  applyPerk(p, recipeId, guest) {
    const P = PERKS[p];
    if (!P) return;
    this.sfx('perk');
    setTimeout(() => this.sfx('coin'), 420);
    if (P.dur) this.perks[p] = P.dur;
    // apply, and say exactly what happened
    let result = '';
    switch (p) {
      case 'bonus':
        this.coins += 25;
        result = '+25 coins added';
        break;
      case 'streak':
        this.streak += 2;
        result = `Tip multiplier is now ×${this.streakMul()}`;
        break;
      case 'drone': {
        let n = 0;
        for (const s of this.sinks) { n += s.dirty; s.setSinkCount(0); }
        for (const s of this.stations) if (s.item && s.item.kind === 'cup' && s.item.dirty) { n += s.item.count; s.takeItem().dispose(); }
        if (this.racks[0]) this.racks[0].setRackCount(this.racks[0].clean + n);
        result = n ? `${n} dirty cup${n > 1 ? 's' : ''} washed` : 'No dirty cups right now';
        break;
      }
      case 'patience': {
        let n = 0;
        for (const g of this.guests) if (g.state === 'wait') { g.patience = Math.min(g.maxPatience, g.patience + g.maxPatience * 0.4); n++; }
        result = n ? `${n} guest${n > 1 ? 's' : ''} calmed down` : 'No one is waiting right now';
        break;
      }
      case 'calm': {
        let n = 0;
        for (const s of this.stations) if (s.fire) { this.extinguish(s); n++; }
        result = n ? `${n} fire${n > 1 ? 's' : ''} put out` : '';
        break;
      }
    }
    this.hud.perkReveal(p, recipeId, result);
    if (guest) {
      // a ring of perk-coloured sparks bursting from the guest
      const c = col(P.color);
      for (let i = 0; i < 36; i++) {
        const a = (i / 36) * Math.PI * 2;
        this.scene.particles[0].emit({ pos: [guest.x, 1.3, guest.z], vel: [Math.cos(a) * 2.6, rand(0.6, 1.6), Math.sin(a) * 2.6], life: 1.0, size: 0.08, size1: 0, color: c, color1: [1, 1, 1], alpha: 1.6, type: 1, drag: 1.5 });
      }
      this.hud.popText(`<span class="pp-ic">${P.icon || '★'}</span> ${P.short || P.name}`, [guest.x, 2.5, guest.z], P.color, true);
    }
  }


  usePress(chef) {
    const H = chef.held;
    if (H && H.throwable) {
      this.throwItem(chef);
      return true;
    }
    return false;
  }
  useHold(chef, dt) {
    const H = chef.held;
    if (H && H.kind === 'ext') {
      // spray foam (guests draw their own copy of this continuous spray)
      this._local(() => this._spray(chef));
      audio.setLoop('hum', 0.4);
      this._sprayT = 0.1;
      for (const s of this.stations) {
        if (!s.fire) continue;
        const vx = s.wx - chef.x, vz = s.wz - chef.z;
        const d = Math.hypot(vx, vz);
        if (d < 2.1 && (vx * chef.fx + vz * chef.fz) / d > 0.5) {
          s.fire.hp -= dt / 1.1;
          if (s.fire.hp <= 0) this.extinguish(s);
        }
      }
      chef.useHeld = true;
      return;
    }
    if (H || chef.job) return;
    const tgt = this.findTarget(chef);
    if (!tgt || !tgt.station) return;
    const s = tgt.station;
    if (s.fire) return;
    if ((s.kind === 'chop' || s.kind === 'grind') && s.item && s.item.kind === 'ing' && s.item.needsPrep) {
      const want = s.kind === 'chop' ? 'chop' : 'grind';
      if (s.item.def.prep !== want) {
        if (!chef._prepWarn || chef._prepWarn < this.time) {
          this._nope(chef, want === 'chop' ? 'Grind this one!' : 'Chop this one!');
          chef._prepWarn = this.time + 1;
        }
        return;
      }
      chef.job = s;
    } else if (s.kind === 'sink' && s.dirty > 0) {
      chef.job = s;
    }
  }

  /** Is this station still something the chef can keep working on? */
  _jobValid(chef, s) {
    if (chef.held || s.fire || chef.respawn > 0 || chef.falling > 0) return false;
    // the chef has to stay right next to the station, facing it
    const vx = s.wx - chef.x, vz = s.wz - chef.z, d = Math.hypot(vx, vz);
    if (d > 1.35 || (vx * chef.fx + vz * chef.fz) / d < 0.35) return false;
    if (s.kind === 'sink') return s.dirty > 0;
    return !!(s.item && s.item.kind === 'ing' && s.item.needsPrep && s.item.def.prep === (s.kind === 'chop' ? 'chop' : 'grind'));
  }

  /** One tick of chopping / grinding / washing for a chef who is busy at a station. */
  _work(chef, s, dt) {
    chef.useHeld = true;
    s.useT = 0.1;
    chef._sfxT = (chef._sfxT || 0) - dt;
    if (s.kind === 'chop' || s.kind === 'grind') {
      const want = s.kind === 'chop' ? 'chop' : 'grind';
      s.item.prog += dt / (want === 'chop' ? 1.7 : 2.0);
      if (chef._sfxT <= 0) {
        this.sfx(want === 'chop' ? 'chop' : 'grind');
        chef._sfxT = want === 'chop' ? 0.17 : 0.24;
        const c = col(s.item.def.color);
        // (the chopping board throws its own bits, in time with its knife)
        if (want !== 'chop') for (let i = 0; i < 3; i++) this.scene.particles[0].emit({ pos: [s.wx, 1.0, s.wz], vel: [rand(-1, 1), rand(1, 2), rand(-1, 1)], life: 0.5, size: 0.04, size1: 0.01, color: c, alpha: 0.9, gravity: 6, type: 0 });
      }
      if (s.item.prog >= 1) {
        s.item.prepare();
        this.sfx('prepDone');
        chef.job = null;
      }
    } else if (s.kind === 'sink') {
      s.prog += dt / 1.3;
      if (chef._sfxT <= 0) {
        this.sfx('wash');
        chef._sfxT = 0.2;
        for (let i = 0; i < 3; i++) this.scene.particles[1].emit({ pos: [s.wx + rand(-0.2, 0.2), 0.95, s.wz + rand(-0.2, 0.2)], vel: [rand(-0.3, 0.3), rand(0.4, 1), rand(-0.3, 0.3)], life: 0.9, size: 0.05, size1: 0.1, color: [0.9, 0.97, 1], alpha: 0.7, type: 3 });
      }
      if (s.prog >= 1) {
        s.prog = 0;
        s.setSinkCount(s.dirty - 1);
        const rack = this.racks.reduce((a, r) => (!a || dist(r, s) < dist(a, s) ? r : a), null);
        if (rack) rack.setRackCount(rack.clean + 1);
        this.sfx('clean');
        this._washed = true;
        if (s.dirty <= 0) chef.job = null; // keeps washing the whole stack until it's empty
      }
    }
  }
  throwItem(chef) {
    const it = chef.release();
    const p = [chef.x + chef.fx * 0.4, 1.0, chef.z + chef.fz * 0.4];
    this.scene.add(it.node);
    it.node.position = p.slice();
    this.projectiles.push({ item: it, x: p[0], y: p[1], z: p[2], vx: chef.fx * 7.5, vy: 2.2, vz: chef.fz * 7.5, t: 0, thrower: chef });
    this.sfx('throw');
  }
  dropToFloor(chef) {
    const it = chef.release();
    let x = chef.x + chef.fx * 0.45, z = chef.z + chef.fz * 0.45;
    if (this.solidCircle(x, z, 0.1)) { x = chef.x; z = chef.z; }
    this._placeFloor(it, x, z);
    this.sfx('drop');
  }
  _placeFloor(it, x, z) {
    this.scene.add(it.node);
    it.node.position = [x, 0.03, z];
    it.node.rotation = [0, Math.random() * 6, 0];
    it.holder = 'floor';
    this.floorItems.push({ item: it, x, z });
  }
  extinguish(s) {
    if (!s.fire) return;
    this._removeFire(s);
    this.sfx('extinguish');
    for (let i = 0; i < 16; i++) this.scene.particles[1].emit({ pos: [s.wx, 1.1, s.wz], vel: [rand(-0.5, 0.5), rand(0.5, 1.5), rand(-0.5, 0.5)], life: 1.6, size: 0.2, size1: 0.6, color: [0.7, 0.7, 0.75], alpha: 0.5, type: 2, drag: 1 });
  }
  _removeFire(s) {
    s.node.remove(s.fire.node);
    this.scene.lights.splice(this.scene.lights.indexOf(s.fire.light), 1);
    this.baseLights = null;
    s.fire = null;
  }
  _addFire(s) {
    const node = new Node(M.fireMesh(), { castShadow: false });
    node.position = [0, 0.05, 0];
    node.userData.base = 1.6;
    s.node.add(node);
    const light = new Light({ position: [s.wx, 1.5, s.wz], color: '#ff7a2a', intensity: 8, range: 4.5 });
    this.scene.lights.push(light);
    this.baseLights = null;
    s.fire = { hp: 1, t: 0, node, light, spread: 7 + Math.random() * 3 };
  }
  ignite(s) {
    if (s.fire || !s.flammable) return;
    if (this.perkActive('calm')) return;
    this._addFire(s);
    this.sfx('ignite');
    this.shake = 0.5;
    this.hud.popText('FIRE!', [s.wx, 2, s.wz], '#ff7a3a', true);
  }

  // ------------------------------------------------------------ guests
  _spawnGuest() {
    const seats = this.world.seats;
    const busy = new Set(this.guests.map((g) => g.seat));
    const bars = this.stations.filter((s) => s.kind === 'bar');
    const free = seats.map((_, i) => i).filter((i) => !busy.has(i) && !(bars.find((b) => b.x === seats[i]) || {}).item);
    if (!free.length) return false;
    if (this.guests.filter((g) => g.state !== 'gone').length >= SPECIES.length) return false; // one of each species at a time
    const seat = pick(free);
    // choose recipe: avoid immediate repeats, weight simple recipes early
    const recipes = this.level.recipes;
    const elapsed = 1 - this.timeLeft / this.level.time;
    let options = recipes.filter((r) => !this.recentOrders.slice(-2).includes(r));
    if (!options.length) options = recipes;
    if (elapsed < 0.2 && this.served < 2) {
      const simple = options.filter((r) => RECIPES[r].ing.length <= 3 && !RECIPES[r].fizz);
      if (simple.length) options = simple;
    }
    const rid = pick(options);
    this.recentOrders.push(rid);
    if (this.recentOrders.length > 8) this.recentOrders.shift(); // (only the last few matter, and an infinite shift goes on for hours)
    const g = new Guest(this, seat, rid);
    this.guests.push(g);
    return true;
  }
  onGuestSeated(g) {
    this.hud.orderNew(g);
  }
  onGuestFail(g) {
    this.failed++;
    this.streak = 0;
    this.coins = Math.max(0, this.coins - 10);
    this.sfx('angry');
    this.hud.popText('-10', [g.x, 2.0, g.z], '#ff6a6a', true);
    this.hud.orderDone(g, false);
    this.hud.bumpScore(1, true);
  }
  onGuestFinished(g) {
    // dirty cup goes on the bar
    const seatX = this.world.seats[g.seat];
    const bar = this.stations.find((s) => s.kind === 'bar' && s.x === seatX);
    const cup = g.cup;
    g.parts.body.remove(cup.node);
    if (cup.kind === 'bobacup') {
      g.cup = null; // disposable: the guest takes it along
      return;
    }
    cup.setDirty(true);
    if (bar) {
      if (bar.item && bar.item.kind === 'cup' && bar.item.dirty) {
        bar.item.addStack(cup);
        cup.dispose();
      } else if (!bar.item) bar.setItem(cup);
      else if (this.racks[0]) this.racks[0].setRackCount(this.racks[0].clean + 1);
    }
    g.cup = null;
  }

  fxBeam(x, z, color) {
    const c = col(color);
    for (let i = 0; i < 30; i++)
      this.scene.particles[0].emit({ pos: [x + rand(-0.3, 0.3), rand(0, 2.2), z + rand(-0.3, 0.3)], vel: [0, rand(0.5, 2), 0], life: rand(0.5, 1.1), size: 0.05, size1: 0.0, color: c, alpha: 1.5, type: 1 });
    this.scene.particles[0].emit({ pos: [x, 0.1, z], life: 0.6, size: 0.3, size1: 1.2, color: c, alpha: 1, type: 3 });
  }
  fxSpawn(x, z, color) {
    this.fxBeam(x, z, color);
    this.sfx('teleport');
  }
  _splash(pos, color) {
    const c = col(color);
    for (let i = 0; i < 10; i++) this.scene.particles[0].emit({ pos: [pos[0], pos[1] + 0.4, pos[2]], vel: [rand(-1, 1), rand(1.5, 3), rand(-1, 1)], life: 0.6, size: 0.05, size1: 0.01, color: c, alpha: 1, gravity: 8 });
  }

  // ------------------------------------------------------------ update
  update(dt) {
    if (this.mirror) return this._mirrorUpdate(dt);
    this.time += dt;
    this.stateT += dt;
    const t = this.time;
    // --- state machine
    if (this.state === 'intro' && this.stateT > 2.4) {
      this.state = 'countdown';
      this.stateT = 0;
      this._cdStep = -1;
    }
    if (this.state === 'countdown') {
      // 3, 2, 1, Serve! driven by game time, so it pauses with the game
      const step = Math.floor(this.stateT / 0.8);
      if (step !== this._cdStep && step <= 3) {
        this._cdStep = step;
        this.hud.countdownStep(step);
      }
    }
    if (this.state === 'countdown' && this.stateT > 3.2) {
      this.state = 'play';
      this.stateT = 0;
    }
    const playing = this.state === 'play';
    if (playing) {
      this.timeLeft -= dt;
      this.played += dt;
      if (this.timeLeft <= 30 && !this._hurry) {
        this._hurry = true;
        this.hud.banner('Last orders!', '30 seconds left: rush hour in the teahouse', '#ffcf5a');
      }
      this._music();
      if (this.timeLeft <= 10 && Math.ceil(this.timeLeft) !== this._lastTick) {
        this._lastTick = Math.ceil(this.timeLeft);
        this.sfx('timeWarn');
      }
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.state = 'end';
        this.stateT = 0;
        this.sfx('fanfare');
        this.hud.timeUp();
        audio.stopLoops();
      }
    }

    // --- chefs & input
    const canAct = playing;
    for (const h of this.helpers) h.tick(dt);
    const ctrls = {};
    for (const p of this.players) ctrls[p.device] = this.solo && !isBot(p) ? input.readAny() : this.readCtrl(p.device);
    // spectators here have no chef, but their Delegate button still opens the wheel
    for (const p of this.spectators) if (this._isLocal(p)) ctrls[p.device] = this.solo ? input.readAny() : this.readCtrl(p.device);
    this._updateWheel(ctrls, canAct);
    for (const chef of this.chefs) {
      const c = canAct && chef.active ? ctrls[chef.controller] : null;
      chef.useHeld = false;
      chef.update(dt, t, c, this.darkness);
      // moving or dashing away stops the current job (progress is kept)
      if (chef.job && c && (Math.hypot(c.mx, c.mz) > 0.15 || c.dash || c.grab)) chef.job = null;
      if (c && chef.respawn <= 0 && chef.falling <= 0) {
        if (c.grab) this.grab(chef);
        if (c.use) this.usePress(chef);
        if (c.useHeld || (c.use && !chef.held)) this.useHold(chef, dt);
      }
      // a started job keeps going while the chef stays put
      if (chef.job) {
        if (this._jobValid(chef, chef.job) && canAct) this._work(chef, chef.job, dt);
        else chef.job = null;
      }
    }
    // chef-chef pushing (Overcooked-style): whoever walks into another chef shoves them along.
    // A busy chef keeps working until they're shoved too far from their station (see _jobValid).
    const R2 = 0.62;
    for (let pass = 0; pass < 3; pass++)
      for (let i = 0; i < this.chefs.length; i++)
        for (let j = i + 1; j < this.chefs.length; j++) {
          const a = this.chefs[i], b = this.chefs[j];
          if (a.respawn > 0 || b.respawn > 0 || a.falling > 0 || b.falling > 0) continue;
          let dx = b.x - a.x, dz = b.z - a.z, d = Math.hypot(dx, dz);
          if (d >= R2) continue;
          if (d < 1e-4) { dx = 1; dz = 0; d = 1e-4; }
          const nx = dx / d, nz = dz / d, over = R2 - d;
          // how hard each one is walking into the other decides who gives way
          const pa = Math.max(0, a.vx * nx + a.vz * nz), pb = Math.max(0, -(b.vx * nx + b.vz * nz));
          let shareA = (pb + 0.2) / (pa + pb + 0.4); // fraction of the overlap that a gives way
          shareA = clamp(shareA, 0.22, 0.78);
          const bx0 = b.x, bz0 = b.z;
          [b.x, b.z] = this.pushOut(b.x + nx * over * (1 - shareA), b.z + nz * over * (1 - shareA), 0.3);
          // if b was stopped by a wall, a takes up the rest
          const moved = (b.x - bx0) * nx + (b.z - bz0) * nz;
          const rest = over - moved;
          if (rest > 0) [a.x, a.z] = this.pushOut(a.x - nx * rest, a.z - nz * rest, 0.3);
        }
    // highlight targets (a bot's aim isn't lit up: it would only distract)
    for (const chef of this.chefs) {
      if (!chef.active || chef.respawn > 0) continue;
      const tg = this.findTarget(chef);
      if (!chef.isBot) this._highlight(tg);
      chef.target = tg;
    }
    if (this._sprayT > 0) this._sprayT -= dt;
    else audio.setLoop('hum', 0);

    // --- stations & vessels
    const boost = this.perkActive('boost') ? 2 : 1;
    this._local(() => {
      for (const s of this.stations) s.update(dt, t);
    });
    for (const v of this.vessels) {
      const st = v.holder;
      if (!playing || !this._brewing(v)) continue;
      v.brewed = Math.min(v.need, v.brewed + dt * boost); // (never banks brewing ahead for what goes in next)
      if (!v.result && !v.waiting && v.brewed >= v.need) {
        v.finish();
        if (v.waiting) {
          // one ingredient short: it simply waits for it (only a wrong ingredient makes sludge)
          this.sfx('wrong');
          this.hud.popText('Missing an ingredient! Add it to finish the drink', [st.wx, 2.1, st.wz], '#ffd27a', true);
        } else {
          if (v.result === 'sludge') this._sludgePop(st);
          else this.sfx('brewDone');
          const [x, y, z] = st.node.position;
          for (let i = 0; i < 12; i++) this.scene.particles[0].emit({ pos: [x, y + 0.5, z], vel: [rand(-0.8, 0.8), rand(1, 2), rand(-0.8, 0.8)], life: 0.8, size: 0.06, size1: 0, color: col((RECIPES[v.result] || SLUDGE).color), alpha: 1.5, type: 1, gravity: 1 });
        }
      }
      if (v.result) {
        v.over += dt;
        if (v.app === 'kettle' && v.over > 11) {
          v.burn();
          this.sfx('burnt');
          this.ignite(st);
        }
      }
    }
    // carbonators
    for (const s of this.stations) {
      if (s.kind !== 'carbonator' || !s.item || s.fire) continue;
      const cup = s.item;
      if (cup.drink && !cup.fizzy) {
        cup.fizzProg += dt / 2.8 * boost;
        if (cup.fizzProg >= 1) {
          cup.setFizzy(true);
          this.sfx('fizz');
        }
      }
    }
    // sealers
    for (const s of this.stations) {
      if (s.kind !== 'sealer' || !s.item || s.fire || !playing) continue;
      const cup = s.item;
      if (!cup.sealable) continue;
      cup.sealProg += (dt / 1.4) * boost;
      if (cup.sealProg >= 1) {
        cup.seal();
        this.sfx('seal');
        for (let i = 0; i < 14; i++) this.scene.particles[1].emit({ pos: [s.wx + rand(-0.15, 0.15), COUNTER_H + 0.42, s.wz + rand(-0.12, 0.16)], vel: [rand(-0.4, 0.4), rand(0.2, 0.7), rand(-0.4, 0.4)], life: 0.8, size: 0.05, size1: 0.18, color: [1, 0.95, 1], alpha: 0.4, type: 2, drag: 2 });
      }
    }
    // conveyors & chute
    this._updateConveyors(dt);
    // fires burn what sits in them, and spread
    for (const s of this.stations) {
      if (!s.fire) continue;
      const f = s.fire;
      f.t += dt;
      if (s.item && s.item.kind === 'ing' && f.t > 2) s.takeItem().dispose();
      f.spread -= dt;
      if (f.spread <= 0 && playing) {
        f.spread = 10 + Math.random() * 4;
        const nb = [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => this.tileAt(s.x + dx, s.y + dy)).filter((t) => t && t.st && t.st.flammable && !t.st.fire);
        if (nb.length && Math.random() < 0.45) this.ignite(pick(nb).st);
      }
    }

    // --- floor items, projectiles
    this._updateProjectiles(dt);

    // --- guests
    if (playing) {
      this.spawnTimer -= dt;
      const waiting = this.guests.filter((g) => g.state === 'wait' || g.state === 'arrive').length;
      const maxG = Math.min(this.level.maxGuests + (this.players.length >= 2 ? 1 : 0), this.world.seats.length);
      if (waiting === 0 && this.spawnTimer > 2) this.spawnTimer = 2;
      if (this.spawnTimer <= 0 && waiting < maxG) {
        if (this._spawnGuest()) this.spawnTimer = (this.level.spawn * rand(0.8, 1.15) * 1.1 * (this._hurry ? 0.8 : 1)) / crewScale(this.players.length);
        else this.spawnTimer = 1;
      }
    }
    this._local(() => {
      for (const g of this.guests) g.update(dt, t);
    });
    for (const g of this.guests.filter((g) => g.state === 'gone')) g.dispose();
    this.guests = this.guests.filter((g) => g.state !== 'gone');

    // --- perks
    for (const k in this.perks) this.perks[k] = Math.max(0, this.perks[k] - dt);

    // --- twists
    this._updateTwists(dt, t, playing);
    this._local(() => this._ambient(dt, t, playing));
    this._present(dt, t);
  }

  /** Guest: show the host's kitchen. Only our own chefs move here; everything else comes from the host. */
  _mirrorUpdate(dt) {
    const net = this.net;
    net.applyPending(this);
    this.time += dt;
    this.stateT += dt;
    const t = this.time;
    const playing = this.state === 'play';
    if (playing) {
      this.timeLeft = Math.max(0, this.timeLeft - dt);
      this.played += dt;
      this._music();
    }
    const ctrls = {};
    for (const chef of this.chefs) if (playing && chef.active && chef.local && !chef.netLock) ctrls[chef.controller] = net.readLocal(chef.controller);
    for (const p of this.spectators) if (playing && this._isLocal(p)) ctrls[p.device] = net.readLocal(p.dev);
    this._updateWheel(ctrls, playing);
    for (const chef of this.chefs) chef.update(dt, t, ctrls[chef.controller] || null, this.darkness);
    this._pushLocalChefs();
    for (const chef of this.chefs) {
      if (chef.job) chef.job.useT = 0.1;
      if (!chef.active || chef.respawn > 0) continue;
      const tg = this.findTarget(chef);
      if (!chef.isBot) this._highlight(tg);
      chef.target = tg;
    }
    for (const s of this.stations) s.update(dt, t);
    let spraying = false;
    for (const chef of this.chefs) {
      if (!chef.held || chef.held.kind !== 'ext' || !chef.useHeld || chef.respawn > 0) continue;
      spraying = true;
      this._spray(chef);
    }
    audio.setLoop('hum', spraying ? 0.4 : 0);
    this._poseConveyors(dt, true);
    for (const p of this.projectiles) {
      p.vy -= 14 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.item.node.position = [p.x, p.y, p.z];
      p.item.node.rotation[0] += dt * 8;
    }
    for (const g of this.guests) g.mirror(dt, t);
    this.darkness = lerp(this.darkness, this.darkTarget, 1 - Math.exp(-dt * 10));
    this._poseBridges(t, playing);
    this._ambient(dt, t, playing);
    this._present(dt, t);
  }
  /** Guest: our own chefs don't walk through the others. */
  _pushLocalChefs() {
    const R2 = 0.62;
    for (const a of this.chefs) {
      if (!a.local || a.netLock || a.respawn > 0 || a.falling > 0) continue;
      for (const b of this.chefs) {
        if (b === a || b.respawn > 0 || b.falling > 0) continue;
        let dx = a.x - b.x, dz = a.z - b.z, d = Math.hypot(dx, dz);
        if (d >= R2) continue;
        if (d < 1e-4) { dx = 1; dz = 0; d = 1e-4; }
        const over = (R2 - d) * (b.local ? 0.5 : 0.6);
        [a.x, a.z] = this.pushOut(a.x + (dx / d) * over, a.z + (dz / d) * over, 0.3);
      }
    }
  }
  // ------------------------------------------------------------ Delegate wheel
  /**
   * Hold the Delegate key to open the wheel, aim at a task (mouse, or the move stick / keys), release to
   * switch it on or off. While it's open, the chef who opened it stands still. A spectator's wheel opens mid-view.
   */
  _updateWheel(ctrls, canAct) {
    const w = this.wheel;
    if (w) {
      const c = ctrls[w.device];
      if (!canAct || !c) return this._closeWheel(false);
      const [dx, dy] = input.takeMouse();
      w.mx += dx;
      w.my += dy;
      const ml = Math.hypot(w.mx, w.my);
      if (ml > 70) { w.mx *= 70 / ml; w.my *= 70 / ml; }
      let ax = 0, ay = 0;
      if (Math.hypot(c.mx, c.mz) > 0.5) {
        [ax, ay] = [c.mx, c.mz];
        w.mx = w.my = 0;
      } else if (Math.hypot(w.mx, w.my) > 30) [ax, ay] = [w.mx, w.my];
      if (ax || ay) {
        // segment 0 at the top, then clockwise
        const n = w.tasks.length, a = (Math.atan2(ax, -ay) + Math.PI * 2) % (Math.PI * 2);
        // (a hair of bias settles aims that fall right on a border, like a key pointing between two segments)
        const sel = (((Math.round(a / ((Math.PI * 2) / n) - 1e-3)) % n) + n) % n;
        if (sel !== w.sel) {
          w.sel = sel;
          audio.sfx('wheelTick');
        }
      }
      if (!c.swapHeld) return this._closeWheel(true);
      // the wheel has the stick and keys now
      ctrls[w.device] = { mx: 0, mz: 0, grab: false, use: false, useHeld: false, dash: false, swap: false, swapHeld: true };
      return;
    }
    if (!canAct) return;
    for (const dev in ctrls) {
      const c = ctrls[dev];
      if (!c || !c.swap || dev.startsWith('bot') || dev.startsWith('net:')) continue;
      const seat = this._seatOf(dev);
      if (!seat) continue;
      const { owner, anchor } = seat;
      const tasks = delegateTasks(this);
      const note = (text) => this.app.hud.popText(text, [anchor.x, 1.9, anchor.z], '#ffd27a');
      if (!this.chefs.some((x) => x.isBot)) return note('Delegate: add a bot helper on the Crew screen first');
      if (!this.ownedBots(owner).length) return note('Delegate: the bots are shared out, and none of them is yours this shift');
      if (!tasks.length) return note('Nothing to delegate on this shift');
      input.takeMouse();
      this.wheel = { device: dev, dev: seat.dev, owner, anchor, tasks, sel: null, mx: 0, my: 0 };
      audio.sfx('wheelOpen');
      ctrls[dev] = { mx: 0, mz: 0, grab: false, use: false, useHeld: false, dash: false, swap: false, swapHeld: true };
      return;
    }
  }
  /**
   * Whose Delegate button this device is on this screen: their owner key, their device as the host knows it, and where
   * their wheel and its notes appear (over their chef, or mid-view in the spectators' colour for someone watching).
   * Null when nobody here plays with that device.
   */
  _seatOf(dev) {
    const chef = this.chefs.find((x) => x.controller === dev && x.local);
    if (chef) return { owner: this.ownerKey(this.players[chef.index]), dev: this.players[chef.index].dev, anchor: chef };
    const p = this.spectators.find((q) => q.device === dev && this._isLocal(q));
    if (!p) return null;
    const [x, , z] = this.camera.target;
    return { owner: this.ownerKey(p), dev: p.dev, anchor: { x, z, color: SPECTATOR_COLOR } };
  }
  /** Close the Delegate wheel without switching anything. True if it was open. */
  cancelWheel() {
    if (!this.wheel) return false;
    this.wheelClosed = { ...this.wheel, chose: false };
    this.wheel = null;
    audio.sfx('uiBack');
    return true;
  }
  _closeWheel(apply) {
    const w = this.wheel;
    this.wheel = null;
    this.wheelClosed = { ...w, chose: apply && w.sel != null }; // the HUD plays the wheel out
    if (!apply || w.sel == null) return;
    const task = w.tasks[w.sel];
    const on = !this.prefsOf(w.owner)[task.id];
    this.toggleDelegate(w.owner, task.id, on);
    if (this.mirror) this.net.delegate(w.dev, task.id, on);
    audio.sfx(on ? 'delegateOn' : 'delegateOff');
    this.app.hud.popText(`${this.app.hud.glyph(task.glyph, 'ic pop-ic')} ${task.name} ${on ? 'on' : 'off'}`, [w.anchor.x, 1.9, w.anchor.z], on ? '#9dffb0' : '#c9c3e6');
  }
  /** Switch a Delegate task on or off for one person's bots (their own choices are kept in their own save). */
  toggleDelegate(key, id, on) {
    this.prefsOf(key)[id] = on;
    if (this._prefKey(key)) this.app.persist();
    else if (this.net && this.net.prefs) this.net.prefs[key] = { ...this.prefsOf(key) }; // (a guest's: for the next shift too)
  }
  _music() {
    // a touch faster in the last 30s, then accelerating with the clock in the last 15s
    audio.intensity = this.timeLeft <= 30 ? 1 : 0;
    audio.tempoMul = this.timeLeft > 30 ? 1 : this.timeLeft > 15 ? 1 + (1 - (this.timeLeft - 15) / 15) * 0.12 : 1.12 + (1 - this.timeLeft / 15) * 0.43;
  }
  /** A vessel on its own base, with something in it that can still cook. */
  _brewing(v) {
    const st = v.holder;
    const onBase = st && st.kind && ((v.app === 'kettle' && st.kind === 'stove') || (v.app === 'blender' && st.kind === 'blender'));
    return !!(onBase && !st.fire && v.contents.length && !v.burnt);
  }
  /** Run fn without sending its particles to guests: they draw continuous effects themselves. */
  _local(fn) {
    const rec = this._rec;
    this._rec = false;
    try {
      fn();
    } finally {
      this._rec = rec;
    }
  }
  /**
   * Who takes which guest, for every chef carrying a drink ready to serve (bots and online players alike): the guests
   * waiting for a drink are shared out among the chefs carrying it, the most urgent guest to the carrier nearest them,
   * so two chefs never head for one guest. A carrier keeps their guest until another is clearly nearer. Guests left
   * over go, one each, to the carrier nearest them as a next stop. A carrier left without a guest gets `yield`: the guest
   * a nearer chef is taking. Chef → { guest, next, yield }.
   */
  deliveries() {
    const out = new Map(), kept = this._delivered || new Map();
    const carriers = this.chefs.filter((c) => c.held && c.respawn <= 0 && c.held.drink && RECIPES[c.held.drink] && servable(c.held, RECIPES[c.held.drink]));
    const away = (c, q) => Math.hypot(q.x - c.x, q.z - c.z) - (kept.get(c) === q.id ? 1.5 : 0);
    const nearest = (list, q) => list.reduce((a, c) => (!a || away(c, q) < away(a, q) ? c : a), null);
    for (const id of new Set(carriers.map((c) => c.held.drink))) {
      const free = carriers.filter((c) => c.held.drink === id);
      const guests = this.guests.filter((q) => q.state === 'wait' && q.recipe.id === id).sort((a, b) => a.patience - b.patience);
      const taking = [];
      for (const q of guests) {
        const c = nearest(free, q);
        if (!c) {
          const by = nearest(taking.filter((o) => !out.get(o).next), q);
          if (by) out.get(by).next = q;
          continue;
        }
        free.splice(free.indexOf(c), 1);
        taking.push(c);
        out.set(c, { guest: q, next: null, yield: null });
      }
      for (const c of free) {
        const by = taking.reduce((a, o) => (!a || Math.hypot(o.x - c.x, o.z - c.z) < Math.hypot(a.x - c.x, a.z - c.z) ? o : a), null);
        out.set(c, { guest: null, next: null, yield: by ? out.get(by).guest : null });
      }
    }
    this._delivered = new Map([...out].filter(([, d]) => d.guest).map(([c, d]) => [c, d.guest.id]));
    return out;
  }
  /**
   * Carrying a finished order: a soft stream of light in the chef's colour drifts along a gentle arc from them to the
   * guest the delivery share-out gives them, with a brighter pulse travelling along it; a carrier a nearer chef is
   * covering keeps just a thin, dim thread to that guest. Only for people's own chefs on their own screen.
   */
  _guideFlow(dt, t) {
    const P = this.scene.particles[0];
    for (const [c, d] of this.deliveries()) {
      if (!c.local || c.isBot) continue;
      const tint = col(c.color);
      if (d.guest) {
        this._beam(P, c, d.guest, tint, dt, t, true);
        if (d.next) this._beam(P, c, d.next, tint, dt, t, true);
      } else if (d.yield) this._beam(P, c, d.yield, tint, dt, t, false);
    }
  }
  /** One guiding arc from a chef to a guest. full: motes, thread and pulse; otherwise a thin, dim thread alone. */
  _beam(P, c, q, tint, dt, t, full) {
    const a = [c.x, 1.15, c.z], b = [q.x, 1.55, q.z];
    const dist = Math.hypot(b[0] - a[0], b[2] - a[2]);
    if (dist < 0.8) return;
    const m = [(a[0] + b[0]) / 2, Math.max(a[1], b[1]) + 0.35 + dist * 0.12, (a[2] + b[2]) / 2];
    const at = (u) => {
      const w = 1 - u;
      return [0, 1, 2].map((k) => w * w * a[k] + 2 * w * u * m[k] + u * u * b[k]);
    };
    if (!full) {
      for (let k = 0; k < 2; k++) P.emit({ pos: at(Math.random()), vel: [0, 0.02, 0], life: 0.3, size: 0.013, size1: 0.01, color: tint, color1: tint, alpha: 0.18, alpha1: 0, type: 0 });
      return;
    }
    // motes drifting along the arc, each fading from the chef's colour to white
    const rate = 44 * dt * Math.min(2, dist / 3 + 0.6);
    const n = Math.floor(rate) + (Math.random() < rate % 1 ? 1 : 0);
    for (let k = 0; k < n; k++) {
      const u = Math.random(), p = at(u), p2 = at(Math.min(1, u + 0.05));
      const dx = p2[0] - p[0], dy = p2[1] - p[1], dz = p2[2] - p[2], l = Math.hypot(dx, dy, dz) || 1, sp = dist * 0.45;
      const wob = Math.sin(t * 3 + u * 9) * 0.06;
      P.emit({ pos: [p[0] + wob, p[1] + Math.cos(t * 2.3 + u * 7) * 0.05, p[2] - wob], vel: [(dx / l) * sp, (dy / l) * sp, (dz / l) * sp], life: 0.6 + Math.random() * 0.4, size: 0.045 + Math.random() * 0.035, size1: 0.008, color: tint, color1: [1, 1, 1], alpha: 0.5, alpha1: 0, type: 0, drag: 0.6 });
    }
    // a faint steady thread, so the path reads as one line
    for (let k = 0; k < 2; k++) {
      const u = Math.random(), p = at(u);
      P.emit({ pos: p, vel: [0, 0.03, 0], life: 0.25, size: 0.022, size1: 0.018, color: tint, color1: tint, alpha: 0.28, alpha1: 0, type: 0 });
    }
    // a brighter pulse travelling from chef to guest
    const p = at((t * 0.55 + q.id * 0.37) % 1);
    if (Math.random() < dt * 50) P.emit({ pos: p, vel: [0, 0.05, 0], life: 0.4, size: 0.09, size1: 0.025, color: [1, 1, 1], color1: tint, alpha: 0.6, alpha1: 0, type: 0 });
  }
  /** Continuous effects and sound loops, worked out from the kitchen as it is (host and guests alike). */
  _ambient(dt, t, playing) {
    if (playing) this._guideFlow(dt, t);
    let blending = 0, boiling = 0, whistle = 0;
    for (const v of this.vessels) {
      const st = v.holder;
      if (playing && this._brewing(v) && !v.waiting) {
        if (v.app === 'blender') {
          blending++;
          v.slosh(t);
        } else {
          boiling++;
          if (v.result && v.over > 4) whistle = 1;
          // steam
          if (Math.random() < dt * (v.result ? 18 : 8)) {
            const [x, y, z] = st.node.position;
            this.scene.particles[1].emit({ pos: [x + 0.22, y + 0.35, z], vel: [rand(0.1, 0.4), rand(0.6, 1.2), rand(-0.1, 0.1)], life: 1.6, size: 0.06, size1: 0.35, color: v.over > 4 ? [1, 0.7, 0.6] : [1, 1, 1], alpha: 0.35, type: 2, drag: 0.8 });
          }
        }
      } else if (v.app === 'blender') {
        v.slosh(null);
      }
    }
    audio.setLoop('blender', Math.min(1, blending * 0.6));
    audio.setLoop('boil', Math.min(1, boiling * 0.5));
    audio.setLoop('whistle', whistle);
    // carbonating cups bubble
    for (const s of this.stations) {
      if (s.kind !== 'carbonator' || !s.item || s.fire || !s.item.drink || s.item.fizzy) continue;
      if (Math.random() < dt * 20) this.scene.particles[0].emit({ pos: [s.wx + rand(-0.08, 0.08), 1.05, s.wz + rand(-0.08, 0.08)], vel: [0, rand(0.4, 0.9), 0], life: 0.6, size: 0.02, size1: 0.03, color: [0.7, 1, 0.95], alpha: 0.8, type: 3 });
    }
    // fizzy cups bubble anywhere
    this._forEachItem((it, pos) => {
      if (it.kind === 'cup' && it.fizzy && Math.random() < dt * 6) this.scene.particles[0].emit({ pos: [pos[0] + rand(-0.06, 0.06), pos[1] + 0.2, pos[2] + rand(-0.06, 0.06)], vel: [0, rand(0.3, 0.6), 0], life: 0.5, size: 0.015, size1: 0.02, color: [0.8, 1, 1], alpha: 0.8, type: 3 });
      if (it.kind === 'ing' && it.id === 'dryice' && Math.random() < dt * 5) this.scene.particles[1].emit({ pos: [pos[0], pos[1] + 0.1, pos[2]], vel: [rand(-0.2, 0.2), -0.05, rand(-0.2, 0.2)], life: 1.4, size: 0.05, size1: 0.2, color: [0.9, 0.95, 1], alpha: 0.35, type: 2, drag: 1.5 });
    });
    // fires flicker, glow and smoke
    let fireLevel = 0;
    for (const s of this.stations) {
      if (!s.fire) continue;
      fireLevel++;
      const f = s.fire;
      const fb = 1.6 * Math.max(0.35, f.hp);
      f.node.scale = [fb * (1 + Math.sin(t * 17) * 0.1), fb * (1 + Math.sin(t * 13 + 1) * 0.15), fb * (1 + Math.cos(t * 15) * 0.1)];
      f.node.rotation[1] += dt * 3;
      f.light.intensity = 7 + Math.sin(t * 23) * 1.5 + Math.sin(t * 9) * 1;
      if (Math.random() < dt * 25) this.scene.particles[0].emit({ pos: [s.wx + rand(-0.25, 0.25), 1.2, s.wz + rand(-0.25, 0.25)], vel: [rand(-0.3, 0.3), rand(1.5, 3), rand(-0.3, 0.3)], life: 0.7, size: 0.12, size1: 0.02, color: [1, 0.6, 0.2], color1: [1, 0.2, 0.05], alpha: 1.4 });
      if (Math.random() < dt * 6) this.scene.particles[1].emit({ pos: [s.wx, 1.6, s.wz], vel: [rand(-0.2, 0.2), rand(0.8, 1.4), rand(-0.2, 0.2)], life: 2, size: 0.2, size1: 0.7, color: [0.12, 0.1, 0.1], alpha: 0.45, type: 2 });
    }
    audio.setLoop('fire', Math.min(1, fireLevel * 0.5));
    // lights & world anim
    this._updateLights(dt, t);
    for (const l of this.world.lights.shafts) l.tint[3] = (l.userData.a ?? 0.05) * (1 + Math.sin(t * 0.7 + l.position[0]) * 0.2) * (1 - this.darkness * 0.95);
    for (const tp of this.world.teleports) {
      tp.beam.rotation[1] += dt;
      tp.beam.tint[3] = 0.2 + Math.sin(t * 3 + tp.x) * 0.06;
      tp.light.intensity = 2.2 + Math.sin(t * 4) * 0.5;
    }
    // ambient dust motes
    if (Math.random() < dt * 10) {
      const b = this.world.bounds;
      this.scene.particles[0].emit({ pos: [rand(b.x0, b.x1), rand(0.3, 2.5), rand(b.zTop, b.zBot)], vel: [rand(-0.05, 0.05), rand(0.02, 0.1), rand(-0.05, 0.05)], life: 5, size: 0.015, size1: 0.015, color: col('#ffe8c0'), alpha: 0.7, type: 0 });
    }
  }
  /** Camera, coach and HUD: the last step of every frame. */
  _present(dt, t) {
    this.shake = Math.max(0, this.shake - dt * 1.5);
    this.scene.flash = Math.max(0, this.scene.flash - dt * 1.2);
    this.scene.particles.forEach((p) => p.update(dt));
    this._placeCamera(dt);
    this._updateCoach(dt, t);
    this.hud.update(this, dt);
  }

  _forEachItem(fn) {
    const tmp = [0, 0, 0];
    for (const s of this.stations) if (s.item) fn(s.item, s.item.node.worldPos(tmp));
    for (const c of this.chefs) if (c.held) fn(c.held, c.held.node.worldPos(tmp));
    for (const f of this.floorItems) fn(f.item, f.item.node.worldPos(tmp));
    for (const p of this.projectiles) fn(p.item, [p.x, p.y, p.z]);
    for (const g of this.guests) if (g.cup) fn(g.cup, g.cup.node.worldPos(tmp));
  }

  _updateConveyors(dt) {
    const conv = this.stations.filter((s) => s.kind === 'conveyor');
    if (!conv.length) return;
    const moved = new Set();
    const SPEED = BELT_SPEED; // tiles per second: the same speed the belt's arrows scroll
    const GAP = 0.72; // closest two items may sit, in tiles
    // items glide forward and queue up smoothly: never faster than the belt, never snapping back
    for (const s of conv) {
      if (!s.item || moved.has(s.item)) continue;
      const nt = this.tileAt(s.x + s.dir[0], s.y + s.dir[1]);
      const n = nt && nt.st;
      const t0 = s.beltT ?? 0.5;
      const onward = n && n.kind === 'conveyor';
      // at the end of the line an item rides to the edge and shrinks away, so the belt never backs up
      const limit = onward ? (n.item ? Math.min(0.98, 1 + (n.beltT ?? 0.5) - GAP) : 1) : 1 + BELT_VANISH;
      let t1 = t0 + dt * SPEED;
      if (t1 > limit) t1 = Math.max(t0, limit); // hold position (don't pull it backwards)
      s.beltT = t1;
      if (!onward && t1 >= 1 + BELT_VANISH) {
        const [x, z] = this._beltSpot(s);
        for (let i = 0; i < 8; i++) this.scene.particles[0].emit({ pos: [x, COUNTER_H + 0.1, z], vel: [rand(-0.5, 0.5), rand(0.4, 1.2), rand(-0.5, 0.5)], life: 0.5, size: 0.05, size1: 0, color: col(this.theme.accent), type: 1 });
        // kettles, jugs, cups and extinguishers go home; anything else is gone
        this._lostItem(s.takeItem());
        continue;
      }
      if (t1 >= 1 && n && n.kind === 'conveyor' && !n.item) {
        const it = s.takeItem();
        n.setItem(it);
        n.beltT = t1 - 1;
        moved.add(it);
      }
    }
    this._poseConveyors(dt, false);
    // chute feeds
    for (const ch of this.stations.filter((s) => s.kind === 'chute')) {
      ch.timer -= dt;
      if (ch.timer > 0 || this.state !== 'play') continue;
      const first = conv.find((c) => Math.abs(c.x - ch.x) + Math.abs(c.y - ch.y) === 1);
      const onBelt = conv.filter((c) => c.item).length;
      if (first && !first.item && onBelt < Math.max(4, conv.length * 0.5)) {
        const list = this.level.chute;
        ch.queue = (ch.queue + 1) % list.length;
        const it = new Ingredient(list[ch.queue]);
        first.setItem(it);
        first.beltT = 0;
        ch.timer = 2.2;
        this.sfx('place');
        for (let i = 0; i < 8; i++) this.scene.particles[0].emit({ pos: [ch.wx, 1.4, ch.wz], vel: [rand(-0.6, 0.6), rand(0.5, 1.5), rand(-0.6, 0.6)], life: 0.6, size: 0.05, size1: 0, color: col(this.theme.accent), type: 1 });
      } else ch.timer = 0.5;
    }
  }

  /** Where an item riding a belt tile is right now (null if it isn't a belt, or the belt is empty). */
  _beltSpot(s) {
    if (s.kind !== 'conveyor' || !s.item) return null;
    const o = Math.min(s.beltT ?? 0.5, 1) - 0.5;
    return [s.wx + s.dir[0] * o, s.wz + s.dir[1] * o];
  }
  /** Light up what a chef would grab: the station, or on a belt the item riding it. */
  _highlight(tg) {
    if (!tg || !tg.station) return;
    if (this._beltSpot(tg.station)) tg.station.item.highlight = 1;
    else tg.station.highlight = 1;
  }
  /** Items ride along the belt. glide: a guest moves them on between the host's snapshots. */
  _poseConveyors(dt, glide) {
    for (const s of this.stations) {
      if (s.kind !== 'conveyor' || !s.item) continue;
      if (glide) {
        const nt = this.tileAt(s.x + s.dir[0], s.y + s.dir[1]);
        const onward = nt && nt.st && nt.st.kind === 'conveyor';
        s.beltT = Math.min((s.beltT ?? 0.5) + dt * BELT_SPEED, onward ? 0.99 : 1 + BELT_VANISH);
      }
      const bt = s.beltT ?? 0.5;
      const o = Math.min(bt, 1) - 0.5;
      s.item.node.position = [s.dir[0] * o, 0, s.dir[1] * o];
      // shrinking away at the end of the line
      const v = bt > 1 ? Math.max(0, 1 - (bt - 1) / BELT_VANISH) : 1;
      const sc = v * v * (3 - 2 * v);
      s.item.node.scale = [sc, sc, sc];
      // the targeted ingredient glows softly (cleared again when it leaves the belt, see Station.takeItem)
      const it = s.item, h = it.highlight || 0;
      if (it.kind === 'ing' && (h > 0 || it._lit)) {
        const k = h * 0.35;
        it.node.traverse((n) => n.mesh && !n.fx && (n.emissive = [k, k * 0.9, k * 0.7]));
        it._lit = h > 0;
      }
      it.highlight = Math.max(0, h - dt * 6);
    }
  }

  _updateProjectiles(dt) {
    for (const p of this.projectiles) {
      p.t += dt;
      p.vy -= 14 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.item.node.position = [p.x, p.y, p.z];
      p.item.node.rotation[0] += dt * 8;
      // catch by another chef with empty hands
      for (const c of this.chefs) {
        if (c === p.thrower || c.held || c.respawn > 0 || p.t < 0.08) continue;
        if (Math.hypot(c.x - p.x, c.z - p.z) < 0.55 && p.y < 1.6) {
          this.scene.root.remove(p.item.node);
          c.hold(p.item);
          p.done = true;
          this.sfx('pickup');
          break;
        }
      }
      if (p.done) continue;
      const [tx, ty] = this.worldToTile(p.x, p.z);
      const tile = this.tileAt(tx, ty);
      const st = tile && tile.st;
      if (!tile || (tile.ch === ' ' && p.y < 0.2) || (tile.ch === '=' && !this.bridgeOpen(tx, ty) && p.y < 0.2)) {
        // lost to space
        if (p.y < -1 || !tile) { this.scene.root.remove(p.item.node); p.done = true; }
        continue;
      }
      if ((st || tile.ch === 'W' || tile.ch === '~' || tile.ch === 'g') && p.y < COUNTER_H + 0.1) {
        this.scene.root.remove(p.item.node);
        p.done = true;
        // land into station
        if (st && !st.fire) {
          const cup = st.item && st.item.kind === 'bobacup' ? st.item : null;
          if (cup && p.item.kind === 'ing' && p.item.id === 'pearls' && cup.canTakePearls) {
            cup.addPearls();
            p.item.dispose();
            this.sfx('addIng');
            continue;
          }
          if (cup && p.item.kind === 'straw' && cup.sealed && !cup.straw) {
            cup.addStraw();
            p.item.dispose();
            this.sfx('straw');
            continue;
          }
          if (st.item && st.item.kind === 'vessel' && p.item.kind === 'ing' && st.item.canAdd(p.item)) {
            if (st.item.add(p.item) === 'sludge') this._sludgePop(st);
            else this.sfx('addIng');
            this._splash(st.node.position, p.item.def.color);
            continue;
          }
          if (!st.item && st.accepts(p.item) && ['counter', 'chop', 'grind', 'conveyor', 'ext'].includes(st.kind)) {
            st.setItem(p.item);
            if (st.kind === 'conveyor') st.beltT = 0.5;
            this.sfx('place');
            continue;
          }
        }
        // bounce back onto the floor, where a chef can reach it
        const [bx, bz] = this._bounceSpot(p);
        this._placeFloor(p.item, bx, bz);
        this.sfx('drop');
        continue;
      }
      if (p.y <= 0.05) {
        p.done = true;
        this.scene.root.remove(p.item.node);
        this._placeFloor(p.item, p.x, p.z);
        this.sfx('drop');
      }
    }
    this.projectiles = this.projectiles.filter((p) => !p.done);
  }

  /**
   * Where a throw that a station (or the guests' lounge) couldn't take comes down: back along its flight to the first
   * walkable floor, so it never rests on a station's own tile, out of every chef's reach.
   */
  _bounceSpot(p) {
    for (let k = 1; k <= 12; k++) {
      const x = p.x - p.vx * 0.06 * k, z = p.z - p.vz * 0.06 * k;
      const tile = this.tileAt(...this.worldToTile(x, z));
      if (tile && !tile.st && WALK.includes(tile.ch)) return [x, z];
    }
    return this._nearestFree(p.x, p.z);
  }

  _updateTwists(dt, t, playing) {
    const ev = this.events;
    const tw = this.twist;
    // teleporters
    if (this.world.teleports.length) {
      for (const c of this.chefs) {
        if (c.respawn > 0) continue;
        const on = this.world.teleports.find((p) => Math.hypot(p.wx - c.x, p.wz - c.z) < 0.35);
        if (!on) {
          if (!this.world.teleports.some((p) => Math.hypot(p.wx - c.x, p.wz - c.z) < 0.7)) c.tpLock = false;
          continue;
        }
        if (c.tpLock || !playing) continue;
        const dest = this.world.teleports.find((p) => p !== on && p.ch === on.ch);
        if (!dest) continue;
        this.fxBeam(c.x, c.z, this.theme.accent2);
        c.x = dest.wx;
        c.z = dest.wz;
        c.vx = c.vz = 0;
        c.tpLock = true;
        c.warp++;
        c.squash = 1;
        this.fxBeam(c.x, c.z, this.theme.accent2);
        this.sfx('teleport');
      }
    }
    // solar flares
    if ((tw === 'flare' || tw === 'gala') && playing) {
      ev.flareT -= dt;
      if (ev.flareT <= 3 && !ev.flareWarned) {
        ev.flareWarned = true;
        this.hud.banner('Solar flare incoming!', 'Stations may catch fire', '#ff9a3a');
        this.sfx('warning');
      }
      if (ev.flareT <= 0) {
        ev.flareWarned = false;
        ev.flareT = (tw === 'gala' ? 42 : 34) + Math.random() * 10;
        this.scene.flash = 0.8;
        this.scene.flashColor = [1, 0.6, 0.25];
        const cands = this.stations.filter((s) => s.flammable && !s.fire && s.kind !== 'ext');
        const stoves = cands.filter((s) => s.kind === 'stove' || s.kind === 'counter');
        const n = tw === 'gala' ? 1 : 1 + (Math.random() < 0.4 ? 1 : 0);
        for (let i = 0; i < n; i++) {
          const pool = stoves.length ? stoves : cands;
          if (!pool.length) break;
          const s = pick(pool);
          this.ignite(s);
          pool.splice(pool.indexOf(s), 1);
        }
      }
    }
    // blackout
    if ((tw === 'blackout' || tw === 'gala') && playing) {
      if (ev.blackDur > 0) {
        ev.blackDur -= dt;
        if (ev.blackDur <= 0) {
          this.sfx('powerOn');
          ev.blackT = (tw === 'gala' ? 48 : 36) + Math.random() * 8;
        }
      } else {
        ev.blackT -= dt;
        if (ev.blackT <= 2.5 && !ev.blackWarned) {
          ev.blackWarned = true;
          this.hud.banner('Eclipse!', 'The lights are going out...', '#b08aff');
        }
        if (ev.blackT <= 0) {
          ev.blackWarned = false;
          ev.blackDur = tw === 'gala' ? 10 : 13;
          this.sfx('blackout');
        }
      }
    }
    const wantDark = ev.blackDur > 0 && playing ? 1 : 0;
    // flicker when going dark
    this.darkness = lerp(this.darkness, wantDark, 1 - Math.exp(-dt * (wantDark ? 3 : 1.5)));
    // bridges
    if (tw === 'bridge') {
      const br = this.world.bridges;
      if (playing) ev.bridgeT -= dt;
      let target = 1;
      if (ev.bridgePhase === 'ext') {
        target = 1;
        if (ev.bridgeT <= 3 && !ev.bridgeWarned) {
          ev.bridgeWarned = true;
          this.hud.banner('Decks separating!', 'Get off the bridges', '#ff8ad8');
          this.sfx('warning');
        }
        if (ev.bridgeT <= 0) {
          ev.bridgePhase = 'ret';
          ev.bridgeT = 9;
          ev.bridgeWarned = false;
          this.sfx('bridge');
        }
      } else {
        target = 0;
        if (ev.bridgeT <= 0) {
          ev.bridgePhase = 'ext';
          ev.bridgeT = 20 + Math.random() * 4;
          this.sfx('bridge');
        }
      }
      for (const b of br) {
        b.ext = lerp(b.ext, target, 1 - Math.exp(-dt * 3.5));
        if (Math.abs(b.ext - target) < 0.01) b.ext = target;
      }
      this._poseBridges(t, playing);
      // chefs standing on retracted bridge fall
      for (const c of this.chefs) {
        if (c.respawn > 0 || c.falling > 0) continue;
        const [tx, ty] = this.worldToTile(c.x, c.z);
        const tile = this.tileAt(tx, ty);
        if (tile && tile.ch === '=' && !this.bridgeOpen(tx, ty) && this.world.bridges.find((b) => b.x === tx && b.y === ty).ext < 0.5) this._chefFall(c);
      }
      // floor items on bridges are lost
      for (const f of this.floorItems.slice()) {
        const [tx, ty] = this.worldToTile(f.x, f.z);
        const tile = this.tileAt(tx, ty);
        if (tile && tile.ch === '=' && !this.bridgeOpen(tx, ty)) {
          this.floorItems.splice(this.floorItems.indexOf(f), 1);
          this._lostItem(f.item);
        }
      }
    }
  }
  _poseBridges(t, playing) {
    const ev = this.events;
    for (const b of this.world.bridges) {
      b.node.position = [b.homeX + b.side * (1 - b.ext) * 1.3, -(1 - b.ext) * 0.3, b.wz];
      const warn = ev.bridgePhase === 'ext' && ev.bridgeT < 3 && playing;
      b.node.emissive = warn && Math.sin(t * 16) > 0 ? [0.6, 0.1, 0.3] : [0, 0, 0];
    }
  }
  _chefFall(c) {
    c.falling = 0.001;
    c.warp++;
    this.sfx('wrong');
    this.hud.popText('Whoooa!', [c.x, 1.8, c.z], '#ffffff');
    if (c.held) this._lostItem(c.release());
  }
  _lostItem(it) {
    if (it.node.parent) it.node.parent.remove(it.node);
    if (it.kind === 'vessel') {
      it.reset();
      const home = [it.home, ...this.stations.filter((s) => s.kind === (it.app === 'kettle' ? 'stove' : 'blender'))].find((s) => s && !s.item);
      if (home) home.setItem(it);
    } else if (it.kind === 'cup' && this.racks[0]) {
      this.racks[0].setRackCount(this.racks[0].clean + it.count);
    } else if (it.kind === 'ext') {
      const s = this.stations.find((s) => s.kind === 'ext' && !s.item);
      if (s) s.setItem(it);
    }
  }

  _updateLights(dt, t) {
    const s = this.scene, e = s.env, D = this.darkness;
    if (!this.baseStatic) this.baseStatic = new Map(this.world.lights.all.map((l) => [l, l.intensity]));
    const k = 1 - D * 0.94;
    for (const [l, base] of this.baseStatic) l.intensity = base * k * (D > 0.3 && Math.random() < 0.02 ? 0.3 : 1);
    for (const lp of this.world.lights.lamps) {
      lp.node.rotation[2] = Math.sin(t * 0.8 + lp.phase) * 0.03;
    }
    e.sunIntensity = this.baseEnv.sun * (1 - D * 0.96);
    e.skyAmb = this.baseEnv.sky.map((v) => v * (1 - D * 0.85));
    e.groundAmb = this.baseEnv.ground.map((v) => v * (1 - D * 0.85));
    e.envIntensity = this.baseEnv.env * (1 - D * 0.8);
    s.sky.nebIntensity = 1;
    // glow light pool
    const glows = [];
    this._forEachItem((it, pos) => {
      const g = it.glow;
      if (g > 0.3) glows.push({ g, pos: pos.slice(), c: it.glowColor });
    });
    glows.sort((a, b) => b.g - a.g);
    this.glowLights.forEach((l, i) => {
      const g = glows[i];
      if (!g) { l.intensity = 0; return; }
      l.position = [g.pos[0], g.pos[1] + 0.25, g.pos[2]];
      l.color = g.c;
      l.intensity = Math.min(3, g.g) * (1.2 + D * 1.5) * (0.9 + Math.sin(t * 5 + i) * 0.1);
      l.range = 2.2 + D;
    });
  }

  dispose() {
    audio.stopLoops();
    window.removeEventListener('resize', this._onResize);
  }
}

function dist(a, b) {
  return Math.hypot(a.wx - b.wx, a.wz - b.wz);
}